import React, { useEffect, useMemo, useRef, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, limit, onSnapshot, orderBy, query, serverTimestamp, setDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import {
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  Download,
  FileText,
  Globe,
  ExternalLink,
  Languages,
  LifeBuoy,
  MapPin,
  Megaphone,
  Plus,
  Route,
  Save,
  ScanLine,
  Sparkles,
  RotateCcw,
  Upload,
  WalletCards
} from 'lucide-react';
import { db, storage } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { getLaunchTrainingSteps, getPromotionPlanNotice, localizePromotionTask } from './promotionPlanConfig';
import { getStaffTrainingSteps } from './promotionPlanConfig';
import StaffTrainingGuide from './StaffTrainingGuide';
import ClientMediaPackageView from './ItineraryCreator/mediaPackage/ClientMediaPackageView';
import { MediaLaunchTimeline } from './ItineraryCreator/mediaPackage/MediaLaunchCalendar';

const ONBOARDING_COLLECTION = 'onboarding_clients';
const ONBOARDING_PUBLIC_COLLECTION = 'onboarding_public';
const ONBOARDING_SHARED_COLLECTION = 'onboarding_shared';
const MEDIA_PRESS_SHARED_DOC = 'media_press';
const ONBOARDING_SEND_REMINDER_ENDPOINT = `${process.env.REACT_APP_FUNCTIONS_BASE_URL || 'https://us-central1-guidesofia-access-control.cloudfunctions.net'}/sendOnboardingReminderEmail`;
const PUBLIC_ONBOARDING_BASE_URL = process.env.REACT_APP_PUBLIC_ONBOARDING_BASE_URL
  || (typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? window.location.origin
    : 'https://backend.guidesofia.com');
const DEFAULT_BOOKING_LINK = 'https://calendar.app.google/KanCUM3U63WvWr3m8';
const GUIDE_SOFIA_BAR_COLOR = '#2563eb';
const CLIENT_BAR_COLOR = '#16a34a';
const BOTH_BAR_COLOR = '#0be7d2';
const ACTIVITY_LOG_LIMIT = 25;

const STAGES = [
  {
    id: 'kickoffMeeting',
    step: '00',
    title: 'Kickoff Meeting',
    owner: 'Client + GuideSofia',
    ownerType: 'both',
    defaultDurationDays: 1,
    color: BOTH_BAR_COLOR,
    dependsOn: []
  },
  {
    id: 'contract',
    step: '01',
    title: 'Contract review + 50% Deposit',
    owner: 'Client + GuideSofia',
    ownerType: 'both',
    defaultDurationDays: 3,
    color: BOTH_BAR_COLOR,
    dependsOn: ['kickoffMeeting']
  },
  {
    id: 'kickoff',
    step: '02',
    title: 'Content Meeting',
    owner: 'Client + GuideSofia',
    ownerType: 'both',
    defaultDurationDays: 1,
    color: BOTH_BAR_COLOR,
    dependsOn: ['contract']
  },
  {
    id: 'routeMaterials',
    step: '03',
    title: 'Route / Stops + Materials',
    owner: 'Client',
    ownerType: 'client',
    defaultDurationDays: 7,
    color: CLIENT_BAR_COLOR,
    dependsOn: ['kickoff']
  },
  {
    id: 'draft',
    step: '04',
    title: 'Audioguide First Draft',
    owner: 'GuideSofia',
    ownerType: 'guidesofia',
    defaultDurationDays: 5,
    color: GUIDE_SOFIA_BAR_COLOR,
    dependsOn: ['routeMaterials']
  },
  {
    id: 'review',
    step: '06',
    title: 'Client Review',
    owner: 'Client',
    ownerType: 'client',
    defaultDurationDays: 5,
    color: CLIENT_BAR_COLOR,
    dependsOn: ['draft']
  },
  {
    id: 'translations',
    step: '07',
    title: 'Translations + Voice Overs',
    owner: 'GuideSofia',
    ownerType: 'guidesofia',
    defaultDurationDays: 3,
    color: GUIDE_SOFIA_BAR_COLOR,
    dependsOn: ['review']
  },
  {
    id: 'qa',
    step: '08',
    title: 'controle de qualidade geral (humano)',
    owner: 'GuideSofia',
    ownerType: 'guidesofia',
    defaultDurationDays: 5,
    color: GUIDE_SOFIA_BAR_COLOR,
    dependsOn: ['review']
  },
  {
    id: 'midwork',
    step: '09',
    title: 'Ponto de situação intermédio',
    owner: 'Client + GuideSofia',
    ownerType: 'both',
    defaultDurationDays: 1,
    color: BOTH_BAR_COLOR,
    dependsOn: ['qa', 'translations']
  },
  {
    id: 'launch',
    step: '10',
    title: 'Launch Communication',
    owner: 'Client + GuideSofia',
    ownerType: 'both',
    defaultDurationDays: 2,
    color: BOTH_BAR_COLOR,
    dependsOn: ['review']
  }
];

const MATERIAL_ITEMS = [
  { id: 'institution', label: 'Institution / project name', icon: Globe },
  { id: 'contact', label: 'Main contact person', icon: FileText },
  { id: 'objective', label: 'Project objective', icon: Sparkles },
  { id: 'texts', label: 'Texts / docs / PDFs', icon: FileText },
  { id: 'media', label: 'Photos / audio / media', icon: Upload },
  { id: 'venue', label: 'Venue / project context', icon: Globe },
  { id: 'route', label: 'Stops list / route indication', icon: Route },
  { id: 'gps', label: 'GPS points', icon: MapPin },
  { id: 'brand', label: 'Brand guidelines / logo', icon: Globe },
  { id: 'accessibility', label: 'Accessibility needs', icon: LifeBuoy },
  { id: 'launchDate', label: 'Launch date or ideal timeline', icon: CalendarDays }
];

const PAYMENT_ITEMS = [
  { id: 'contractReviewed', label: 'Contract reviewed', type: 'contract' },
  { id: 'contractSigned', label: 'Contract signed', type: 'contract' },
  { id: 'depositPaid', label: '50% deposit paid at signing', type: 'payment' },
  { id: 'finalPaid', label: 'Final 50% paid at guide delivery', type: 'payment' }
];

const ADD_ONS = [
  { id: 'venue-pages', label: 'Venue pages', description: 'Dedicated venue or exhibition landing pages connected to the guide.', icon: Globe },
  { id: 'qr-rollout', label: 'QR rollout', description: 'Extra sa traupport for QR implementation, placement planning and rollout.', icon: ScanLine },
  { id: 'translations', label: 'Extra languages', description: 'Additional translations and voice overs beyond the base scope.', icon: Languages },
  { id: 'virtual-360', label: 'Virtual 360 visit', description: 'A 360-degree virtual experience for remote or hybrid visits.', icon: Globe },
  { id: 'augmented-reality', label: 'Augmented reality', description: 'AR moments that add digital layers to the visitor experience.', icon: Sparkles },
  { id: 'gamification', label: 'Gamification', description: 'Quizzes, challenges or playful interactions for visitors.', icon: Sparkles },
  { id: 'sign-language-pt', label: 'Portuguese sign language', description: 'Accessibility support with Portuguese sign language content.', icon: Languages },
  { id: 'collection-catalog', label: 'Collection catalog', description: 'A digital catalog for the collection or exhibition highlights.', icon: FileText },
  { id: 'surveys', label: 'Surveys', description: 'Visitor surveys or feedback forms connected to the project.', icon: FileText },
  { id: 'advanced-content', label: 'Advanced content', description: 'Richer storytelling, special features or expanded content modules.', icon: Sparkles }
];

const PROJECT_TYPES = [
  { value: 'museum', label: 'Museum Guide' },
  { value: 'municipality', label: 'Municipality / Destination' },
  { value: 'temporary', label: 'Temporary Exhibition' },
  { value: 'pilot', label: 'Pilot Project' },
  { value: 'virtual', label: 'Virtual Tour / 360' }
];
const SCOPE_FEATURES = [
  ['qr', 'QR code recognition'], ['gps', 'Geography-based recognition (GPS)'], ['chatbot', 'Conversational AI chatbot'], ['analytics', 'Visitor analytics dashboard'], ['itineraries', 'Personalized AI itineraries'], ['humanQa', 'Human QA and client approval']
];
const SCOPE_PRODUCT_PROFILES = {
  essential: { label: 'Essential Guide', idealFor: 'Museums with a ready-to-go script.', pois: 'Up to 15 POIs', words: 'Up to 6,000 words', source: 'Client provides final script', revisions: 'No collaborative script revisions included', refreshes: '0 included content refreshes per year', languages: 'Up to 3 languages', features: ['qr', 'gps', 'humanQa'] },
  plus: { label: 'Plus Guide', idealFor: 'The complete Storytelling as a Service solution.', pois: 'Up to 20 POIs', words: 'Up to 8,000 words', source: 'GuideSofia creates the script from raw documents', revisions: 'Up to 2 revision rounds of the initial script', refreshes: 'Up to 2 revision rounds per year on up to 3 POIs', languages: 'Up to 4 languages', features: ['qr', 'gps', 'analytics', 'humanQa'] },
  premium: { label: 'Premium AI', idealFor: 'Flagship institutions seeking the ultimate personalized visitor experience.', pois: 'Up to 25 POIs', words: 'Up to 10,000 words', source: 'GuideSofia creates the script from raw documents', revisions: 'Up to 2 revision rounds of the initial script', refreshes: 'Up to 3 revision rounds per year on up to 5 POIs', languages: 'Up to 7 languages', features: ['qr', 'gps', 'chatbot', 'analytics', 'itineraries', 'humanQa'] }
};
const GUIDE_SOFIA_PROJECT_MANAGERS = [
  {
    value: 'nelio',
    label: 'Nelio',
    name: 'Nelio',
    email: 'nelio@guidesofia.com',
    whatsapp: '+351 928 294 841'
  },
  {
    value: 'amanda',
    label: 'Amanda',
    name: 'Amanda',
    email: 'amanda@guidesofia.com',
    whatsapp: '+351 928 294 841'
  }
];
const GUIDE_SOFIA_PAYMENT_DETAILS = {
  entity: 'OBSIDIANBLOOM - Unipessoal Lda',
  addressLine1: 'Rua Manuel Da Silva Leal, n.º 2, 8 esq, 1600-166',
  addressLine2: 'Lisboa, Portugal',
  taxNumber: 'Nº Contribuinte: 518975185',
  email: 'nelio@guidesofia.com',
  bankName: 'Revolut',
  iban: 'LT153250007050588941'
};
const ONBOARDING_LANGUAGE_OPTIONS = [
  { value: 'pt-PT', label: 'PT-PT' },
  { value: 'en', label: 'English' }
];
const GUIDE_LANGUAGE_OPTIONS = [
  { value: 'pt-PT', label: 'Portuguese (PT-PT)' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'nl', label: 'Dutch' },
  { value: 'ru', label: 'Russian' },
  { value: 'ar', label: 'Arabic' },
  { value: 'zh', label: 'Chinese' },
  { value: 'ja', label: 'Japanese' }
];
const getGuideLanguageState = (client, languageCode) => {
  const state = client?.guideLanguages?.[languageCode] || {};
  const isBaseLanguage = languageCode === client?.baseLanguage;

  return {
    selected: Boolean(state.selected || isBaseLanguage),
    ready: Boolean(state.ready || isBaseLanguage)
  };
};
const EMAIL_TEMPLATES = [
  { id: 'client-kickoff', label: 'Kick-off / share onboarding hub', requiresConfirmation: true },
  { id: 'client-review-request', label: 'Audioguide first draft ready for review', requiresConfirmation: true },
  { id: 'client-overdue', label: 'Client overdue phases', requiresConfirmation: true },
  { id: 'client-materials', label: 'Client missing materials', requiresConfirmation: true },
  { id: 'internal-overdue', label: 'Internal overdue phases', requiresConfirmation: false },
  { id: 'internal-launch-risk', label: 'Internal launch risk', requiresConfirmation: false }
];
const REMINDER_POLICIES = {
  'client-kickoff': { maxSends: null, gapBusinessDays: [0] },
  'client-review-request': { maxSends: null, gapBusinessDays: [0] },
  'client-overdue': { maxSends: 3, gapBusinessDays: [0, 4, 6] },
  'client-materials': { maxSends: 3, gapBusinessDays: [0, 4, 6] },
  'internal-overdue': { maxSends: null, gapBusinessDays: [0, 2, 3, 5] },
  'internal-launch-risk': { maxSends: null, gapBusinessDays: [0, 2, 2, 5] }
};

const HUB_SECTIONS = [
  { id: 'timeline', label: 'Timeline' },
  { id: 'production', label: 'Production' },
  { id: 'review', label: 'Review' },
  { id: 'launch', label: 'Launch' },
  { id: 'add-ons', label: 'Optional add-ons' },
  { id: 'contact', label: 'Contact' },
  { id: 'project-details', label: 'Project details' }
];

const PRODUCTION_SUBSECTIONS = [
  { id: 'materials', label: 'Materials' },
  { id: 'languages', label: 'Languages' }
];

const LAUNCH_SUBSECTIONS = [
  { id: 'qr-codes', label: 'QR Codes', icon: ScanLine },
  { id: 'media-press', label: 'Media Package', icon: Megaphone },
  { id: 'launch', label: 'Launch plan' },
  { id: 'launch-training', label: 'Training' }
];

const PROJECT_SUBSECTIONS = [
  { id: 'documents', label: 'Documents' },
  { id: 'payments', label: 'Payments' },
  { id: 'scope', label: 'Scope & changes' },
  { id: 'roles', label: 'Responsibilities' },
  { id: 'faq', label: 'FAQ' }
];

const LAUNCH_ITEMS = [
  { id: 'contentApproved', label: 'Content approved' },
  { id: 'translationsApproved', label: 'Translations approved' },
  { id: 'audioChecked', label: 'Audio checked' },
  { id: 'qrTested', label: 'QR codes tested' },
  { id: 'qrPlaced', label: 'Physical QR codes placed' },
  { id: 'teamInformed', label: 'Reception / visitor team informed' },
  { id: 'websiteReady', label: 'Website or social post prepared' },
  { id: 'analyticsActive', label: 'Analytics activated' },
  { id: 'launchConfirmed', label: 'Launch date confirmed' }
];

const LAUNCH_RESPONSIBILITIES = [
  { id: 'qrPlacement', label: 'Place QR codes on site', owner: 'client' },
  { id: 'teamTraining', label: 'Train reception / visitor team and share the staff training document', owner: 'client' },
  { id: 'onlinePromotion', label: 'Publish website and social communication', owner: 'client' },
  { id: 'onsiteTest', label: 'Run the final on-site test', owner: 'both' },
  { id: 'launchSignoff', label: 'Confirm public launch', owner: 'both' }
];

const DEFAULT_POST_LAUNCH = {
  followUpDate: '',
  followUpStatus: 'planned',
  followUpNotes: '',
  supportTerms: 'Post-launch edits, seasonal updates, new languages and new stops are planned with GuideSofia according to the agreed package.',
  maintenanceOwner: 'GuideSofia + Client',
  analyticsReview: false,
  caseStudyPermission: 'not-asked'
};

const DEFAULT_LAUNCH_PACKAGE = {
  level: 'supported',
  planningCallDate: '',
  staffTrainingDate: '',
  supportStart: '',
  supportEnd: '',
  launchPlanUrl: '',
  staffCheatSheetUrl: '',
  promotionKitUrl: '',
  technicalTestUrl: '',
  supportProtocolUrl: '',
  postLaunchReviewUrl: ''
};

const DEFAULT_PROMOTION_TASKS = [
  { id: 'teaser', phase: 'before', title: 'Publish a teaser for the new digital visitor experience', guidance: 'Give visitors a reason to look forward to the guide before launch.', example: 'A new way to explore [venue] is coming on [date].', channel: 'Social media', owner: 'Client', plannedDate: '', status: 'planned', clientNote: '', assetUrl: '' },
  { id: 'internal-communication', phase: 'before', title: 'Tell all relevant teams about the launch', guidance: 'Brief reception, visitor services, education, communications and management so the guide is not a surprise on launch day.', example: 'Share the launch date, QR location, staff phrase and support contact.', channel: 'Internal email / meeting', owner: 'Client', plannedDate: '', status: 'planned', clientNote: '', assetUrl: '' },
  { id: 'newsletter-event', phase: 'before', title: 'Prepare and schedule the email or newsletter communication for launch week', guidance: 'Prepare one clear message before launch and schedule it for the week the guide becomes available.', example: 'From [date], visitors can explore [venue] with our digital guide in [languages].', channel: 'Email / newsletter', owner: 'Client', plannedDate: '', status: 'planned', clientNote: '', assetUrl: '' },
  { id: 'website', phase: 'before', title: 'Prepare the website visitor-information update', guidance: 'Place the guide where visitors already look for opening hours, tickets and practical information.', example: 'Start the guide on your phone by scanning the QR code or opening [link].', channel: 'Website', owner: 'Client', plannedDate: '', status: 'planned', clientNote: '', assetUrl: '' },
  { id: 'launch-website', phase: 'launch-day', title: 'Publish the website announcement', guidance: 'Make the guide visible on the official website on launch day.', example: 'Discover [venue] through stories, audio and practical information.', channel: 'Website', owner: 'Client', plannedDate: '', status: 'planned', clientNote: '', assetUrl: '' },
  { id: 'launch-social', phase: 'launch-day', title: 'Publish the launch social post or story', guidance: 'Show the guide in use and give people one clear action: scan or open the link.', example: 'Visit us from [date], scan the GuideSofia QR code and begin your tour.', channel: 'Social media', owner: 'Client', plannedDate: '', status: 'planned', clientNote: '', assetUrl: '' },
  { id: 'signage', phase: 'launch-day', title: 'Activate on-site signage', guidance: 'Place clear QR signs where visitors pause and can understand the benefit of scanning.', example: 'Explore the stories around you. Scan, choose your language and begin.', channel: 'On site', owner: 'Client', plannedDate: '', status: 'planned', clientNote: '', assetUrl: '' },
  { id: 'staff-introduction', phase: 'launch-day', title: 'Have staff actively introduce the guide', guidance: 'A confident recommendation from staff often matters more than another poster.', example: 'We have a digital guide on your phone. Scan this code, choose your language and begin.', channel: 'Visitor interaction', owner: 'Client', plannedDate: '', status: 'planned', clientNote: '', assetUrl: '' },
  { id: 'launch-content', phase: 'launch-day', title: 'Capture a photo or short video showing how visitors use the guide', guidance: 'Use real launch-day material for later reminders and internal learning.', example: 'Show a visitor scanning, choosing a language or listening at a stop.', channel: 'Photo / video', owner: 'Client', plannedDate: '', status: 'planned', clientNote: '', assetUrl: '' },
  { id: 'reminder', phase: 'after', title: 'Publish a reminder post', guidance: 'Repeat the invitation during the first two weeks; one post is rarely enough.', example: 'Have you tried our digital guide yet? Start at reception or the route entrance.', channel: 'Social media', owner: 'Client', plannedDate: '', status: 'planned', clientNote: '', assetUrl: '' },
  { id: 'featured-story', phase: 'after', title: 'Highlight one concrete story from the guide', guidance: 'Choose a real object, place or stop by name. Explain what visitors will discover there, instead of using a generic message.', example: '', channel: 'Social / website', owner: 'Client + GuideSofia', plannedDate: '', status: 'planned', clientNote: '', assetUrl: '' },
  { id: 'access-video', phase: 'after', title: 'Share a short video showing the app in use', guidance: 'Show visitors how to start the guide and what they can discover during the visit.', example: 'Find the QR code at [venue], open the guide and begin your visit.', channel: 'Social / website', owner: 'Client', plannedDate: '', status: 'planned', clientNote: '', assetUrl: '' },
  { id: 'feedback-highlight', phase: 'after', title: 'Share visitor feedback or an early usage highlight', guidance: 'Use real observations to make the experience credible and useful.', example: 'Visitors are using the guide in [language] to explore [stop / route].', channel: 'Social / newsletter', owner: 'Client + GuideSofia', plannedDate: '', status: 'planned', clientNote: '', assetUrl: '' },
  { id: 'seasonal-promotion', phase: 'after', title: 'Connect the guide to an event, weekend or seasonal campaign', guidance: 'Attach the guide to an existing reason to visit rather than promoting it in isolation.', example: 'Explore [event] with the digital guide this weekend.', channel: 'Event / campaign', owner: 'Client', plannedDate: '', status: 'planned', clientNote: '', assetUrl: '' }
];

const createDefaultPromotionPlan = () => ({
  enabled: true,
  clientInstructions: 'Introduce the guide repeatedly across the visitor journey. Use the prepared copy and materials for each activity, then check it off when complete.',
  tasks: DEFAULT_PROMOTION_TASKS.map((task) => ({ ...task, completed: false }))
});

const resolvePromotionCopy = (value, client) => {
  const venue = client?.name || client?.guideName || 'your venue';
  const launchDate = client?.agreedLaunchDate ? formatDate(new Date(`${client.agreedLaunchDate}T00:00:00`)) : 'the launch date';
  const selectedLanguages = Object.entries(client?.guideLanguages || {})
    .filter(([, state]) => state?.selected)
    .map(([code]) => code)
    .join(', ');

  return String(value || '')
    .replaceAll('[venue]', venue)
    .replaceAll('[date]', launchDate)
    .replaceAll('[languages]', selectedLanguages || 'the available languages')
    .replaceAll('[link]', 'the guide link')
    .replaceAll('[object / place]', 'a featured stop')
    .replaceAll('[language]', 'a selected language')
    .replaceAll('[stop / route]', 'the route')
    .replaceAll('[event]', 'your next event');
};

const LAUNCH_ASSET_ITEMS = [
  { id: 'qrCodeLinks', label: 'QR code links' },
  { id: 'promoMaterialsLinks', label: 'Promotion materials links' }
];

const MEDIA_PRESS_ITEMS = [
  { id: 'mediaKitUrl', label: 'Media kit link', placeholder: 'https://drive.google.com/... or https://...' },
  { id: 'pressReleaseUrl', label: 'Press release link', placeholder: 'https://docs.google.com/... or https://...' },
  { id: 'pressCoverageLinks', label: 'Press coverage links', placeholder: 'Museum feature | https://...\nLaunch article | https://...' },
  { id: 'messagingNotes', label: 'Messaging notes', placeholder: 'Approved wording, quote options, hashtags, launch angle...' }
];

const createDefaultMediaPress = () => MEDIA_PRESS_ITEMS.reduce((acc, item) => {
  acc[item.id] = '';
  return acc;
}, {});

const REVIEW_ITEMS = [
  { id: 'clientGuideText', label: 'Guide text reviewed', owner: 'client' },
  { id: 'qaTexts', label: 'Texts quality control (Humano)', owner: 'guidesofia' },
  { id: 'qaQrCodes', label: 'QR codes quality control (Humano)', owner: 'guidesofia' },
  { id: 'qaAudios', label: 'Audio quality control (Humano)', owner: 'guidesofia' }
];

const QR_PLACEMENTS = [
  { id: 'reception', label: 'Reception / ticket office' },
  { id: 'routeStart', label: 'Start of route' },
  { id: 'stops', label: 'Each room, stop or object area' },
  { id: 'printed', label: 'Printed maps / brochures' },
  { id: 'website', label: 'Website' },
  { id: 'social', label: 'Social media / newsletter' }
];

const GUIDE_SOFIA_RESPONSIBILITIES = [
  'Structure the digital guide',
  'Create or adapt scripts',
  'Prepare translations when included',
  'Generate QR codes and guide links',
  'Publish the visitor experience',
  'Provide analytics and technical support'
];

const CLIENT_RESPONSIBILITIES = [
  'Share official materials',
  'Validate historical and factual accuracy',
  'Approve final content',
  'Place QR codes on-site',
  'Promote the guide to visitors',
  'Inform front-desk or visitor-facing teams'
];

const FAQ_ITEMS = [
  {
    question: 'How long does implementation take?',
    answer: 'It depends on the number of stops, languages, review cycles and content complexity. The timeline above shows the current working estimate.'
  },
  {
    question: 'Do we need to have all content ready?',
    answer: 'No. GuideSofia can work from existing materials, or help adapt content into visitor-friendly storytelling.'
  },
  {
    question: 'Who validates historical accuracy?',
    answer: 'The client validates factual and institutional accuracy before the guide is approved for production and launch.'
  },
  {
    question: 'Can the guide be updated later?',
    answer: 'Yes, updates can be planned after launch depending on the package and operational needs.'
  },
  {
    question: 'Can we see visitor data?',
    answer: 'Yes. Analytics can show access volume, languages used, popular stops and opportunities to improve the visitor experience.'
  }
];

const STATUS_OPTIONS = [
  { value: 'todo', label: 'To do' },
  { value: 'active', label: 'Active' },
  { value: 'blocked', label: 'Waiting action' },
  { value: 'done', label: 'Done' }
];

const STATUS_LABELS = {
  todo: 'To do',
  active: 'Active',
  blocked: 'Waiting action',
  done: 'Done'
};

const STATUS_CHIP_CLASSES = {
  todo: 'bg-slate-200 text-slate-700',
  active: 'bg-amber-100 text-amber-800',
  blocked: 'bg-rose-100 text-rose-800',
  done: 'bg-emerald-100 text-emerald-800'
};

const RISK_CHIP_CLASSES = {
  onTrack: 'border border-emerald-200 bg-emerald-100 text-emerald-800',
  atRisk: 'border border-amber-200 bg-amber-100 text-amber-800',
  blocked: 'border border-rose-200 bg-rose-100 text-rose-800'
};

const OWNER_LABELS = {
  client: 'Client',
  guidesofia: 'GuideSofia',
  both: 'Client + GuideSofia'
};

const ADDITIONAL_CONTACTS_PLACEHOLDER = 'Name | email@domain.com | +351...';

const DEFAULT_COLLAPSED_PANELS = {
  clientAccess: true,
  guideSofiaContact: true,
  clientContact: true,
  bookMeeting: true,
  emailReminders: true,
  activityLog: true
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

const toStartOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const toBusinessDay = (date) => {
  const next = toStartOfDay(date);
  while (isWeekend(next)) {
    next.setDate(next.getDate() + 1);
  }
  return next;
};

const addBusinessDays = (date, businessDays) => {
  const next = toBusinessDay(date);
  let remaining = Math.abs(businessDays);
  const direction = businessDays >= 0 ? 1 : -1;

  while (remaining > 0) {
    next.setDate(next.getDate() + direction);
    if (!isWeekend(next)) {
      remaining -= 1;
    }
  }

  return next;
};

const countBusinessDaysInclusive = (startDate, endDate) => {
  let count = 0;
  let cursor = toStartOfDay(startDate);
  const end = toStartOfDay(endDate);

  while (cursor <= end) {
    if (!isWeekend(cursor)) {
      count += 1;
    }
    cursor = addDays(cursor, 1);
  }

  return count;
};

const diffDays = (startDate, endDate) => {
  const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  return Math.round((end - start) / 86400000);
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const parseLabeledLink = (line) => {
  const trimmed = (line || '').trim();
  if (!trimmed) return null;

  const separatorIndex = trimmed.indexOf('|');
  if (separatorIndex === -1) {
    return { label: trimmed, url: trimmed };
  }

  const label = trimmed.slice(0, separatorIndex).trim();
  const url = trimmed.slice(separatorIndex + 1).trim();

  if (!url) {
    return { label: trimmed, url: trimmed };
  }

  return {
    label: label || url,
    url
  };
};

const isValidDate = (value) => value instanceof Date && !Number.isNaN(value.getTime());

const formatDate = (value) => (
  isValidDate(value)
    ? new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(value)
    : 'Not set'
);

const formatDateTime = (value) => (
  isValidDate(value)
    ? new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(value)
    : 'Not set'
);

const countBusinessDaysBetween = (startDate, endDate) => {
  let count = 0;
  let cursor = toStartOfDay(startDate);
  const end = toStartOfDay(endDate);

  while (cursor < end) {
    cursor = addDays(cursor, 1);
    if (!isWeekend(cursor)) {
      count += 1;
    }
  }

  return count;
};

const getBusinessDayDelta = (startDate, endDate) => {
  const normalizedStart = toStartOfDay(startDate);
  const normalizedEnd = toStartOfDay(endDate);

  if (normalizedStart.getTime() === normalizedEnd.getTime()) {
    return 0;
  }

  if (normalizedEnd > normalizedStart) {
    return countBusinessDaysBetween(normalizedStart, normalizedEnd);
  }

  return -countBusinessDaysBetween(normalizedEnd, normalizedStart);
};

const getReminderPolicy = (templateId) => REMINDER_POLICIES[templateId] || { maxSends: null, gapBusinessDays: [0] };

const getActivityMessageForField = (field) => {
  switch (field) {
    case 'stageStatuses':
    case 'stageDurations':
      return 'Updated timeline';
    case 'materials':
    case 'materialsReceived':
      return 'Updated project inputs';
    case 'payments':
      return 'Updated contract or payment status';
    case 'reviewChecklist':
      return 'Updated review checklist';
    case 'launchChecklist':
      return 'Updated launch checklist';
    case 'launchAssets':
      return 'Updated launch assets';
    case 'guideQrImageUrl':
      return 'Updated guide QR image';
    case 'addOns':
      return 'Updated optional add-ons';
    case 'guideLanguages':
      return 'Updated guide languages';
    case 'qrPlacements':
      return 'Updated QR placements';
    default:
      return 'Updated project details';
  }
};

const toDateInputValue = (value) => {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const generateAccessToken = () => {
  const bytes = new Uint8Array(24);
  const cryptoSource = typeof window !== 'undefined' ? window.crypto : null;

  if (cryptoSource?.getRandomValues) {
    cryptoSource.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
};

const getContractStageTitle = (client) => {
  const showContractSteps = (client?.showContractSteps ?? true) !== false;
  const showPaymentSteps = (client?.showPaymentSteps ?? client?.showPayments ?? true) !== false;

  if (showContractSteps && showPaymentSteps) return 'Contract review + 50% Deposit';
  if (showContractSteps) return 'Contract review';
  return '50% Deposit';
};

const buildPublicOnboardingJson = (client) => ({
  id: client.id,
  name: client.name || 'Client Onboarding',
  guideName: client.guideName || '',
  mediaPackageWorkspaceId: client.mediaPackageWorkspaceId || '',
  baseLanguage: client.baseLanguage || 'pt-PT',
  projectType: client.projectType || '',
  reviewDocUrl: client.reviewDocUrl || '',
  meetingLink: client.meetingLink || DEFAULT_BOOKING_LINK,
  documentsUrl: client.documentsUrl || '',
  projectManagerName: client.projectManagerName || '',
  projectManagerEmail: client.projectManagerEmail || '',
  projectManagerWhatsapp: client.projectManagerWhatsapp || '',
  projectManagerAdditionalContacts: client.projectManagerAdditionalContacts || '',
  clientContactName: client.clientContactName || '',
  clientContactEmail: client.clientContactEmail || '',
  clientContactWhatsapp: client.clientContactWhatsapp || '',
  clientAdditionalContacts: client.clientAdditionalContacts || '',
  guideLanguages: client.guideLanguages || {},
  kickoffDate: client.kickoffDate,
  launchCommunicationStartDate: client.launchCommunicationStartDate || '',
  guideOnlineDate: client.guideOnlineDate || '',
  guidePublicReadyDate: client.guidePublicReadyDate || '',
  agreedLaunchDate: client.agreedLaunchDate || '',
  guideQrImageUrl: client.guideQrImageUrl || '',
  qrFilesUrl: client.qrFilesUrl || '',
  activeStageId: client.activeStageId,
  stageDurations: client.stageDurations || {},
  stageStatuses: client.stageStatuses || {},
  materials: client.materials || {},
  materialsReceived: client.materialsReceived || {},
  driveUploadUrl: client.driveUploadUrl || '',
  payments: client.payments || {},
  showContractSteps: client.showContractSteps !== false,
  showPaymentSteps: client.showPaymentSteps ?? client.showPayments ?? true,
  reviewChecklist: client.reviewChecklist || {},
  launchChecklist: client.launchChecklist || {},
  launchResponsibilities: client.launchResponsibilities || {},
  scope: client.scope || {},
  postLaunch: client.postLaunch || DEFAULT_POST_LAUNCH,
  launchPackage: client.launchPackage || DEFAULT_LAUNCH_PACKAGE,
  promotionPlan: client.promotionPlan || createDefaultPromotionPlan(),
  launchAssets: client.launchAssets || {},
  addOns: client.addOns || {},
  reminders: client.reminders || {},
  qrPlacements: client.qrPlacements || {},
  publicAccess: client.publicAccess !== false,
  updatedAt: serverTimestamp()
});

const buildTimelineStages = (kickoffDate, stageDurations, stageStatuses, stages = STAGES) => {
  const baseDate = toBusinessDay(kickoffDate ? new Date(`${kickoffDate}T09:00:00`) : new Date());
  const computedStages = new Map();

  stages.forEach((stage) => {
    const rawDuration = Number(stageDurations?.[stage.id]);
    const durationDays = Number.isFinite(rawDuration) ? Math.max(0, rawDuration) : stage.defaultDurationDays;
    const dependencyCompletionDates = (stage.dependsOn || [])
      .map((dependencyId) => computedStages.get(dependencyId)?.completionDate)
      .filter(Boolean);
    const startDate = stage.id === 'routeMaterials' && computedStages.get('kickoff')
      ? new Date(computedStages.get('kickoff').startDate)
      : stage.id === 'midwork' && computedStages.get('qa')
      ? addBusinessDays(computedStages.get('qa').endDate, -1)
      : dependencyCompletionDates.length > 0
      ? addBusinessDays(new Date(Math.max(...dependencyCompletionDates.map((value) => value.getTime()))), 1)
      : new Date(baseDate);
    const launchQualityStage = stage.id === 'launch' ? computedStages.get('qa') : null;
    const endDate = launchQualityStage
      ? addBusinessDays(launchQualityStage.endDate, durationDays)
      : durationDays === 0 ? new Date(startDate) : addBusinessDays(startDate, durationDays - 1);
    const displayedDurationDays = launchQualityStage
      ? diffDays(startDate, endDate) + 1
      : durationDays;
    const completionDate = launchQualityStage
      ? new Date(endDate)
      : durationDays === 0 ? addBusinessDays(startDate, -1) : new Date(endDate);

    computedStages.set(stage.id, {
      ...stage,
      status: stageStatuses?.[stage.id] || 'todo',
      durationDays: displayedDurationDays,
      configuredDurationDays: durationDays,
      startDate,
      endDate,
      completionDate
    });
  });

  return stages.map((stage) => computedStages.get(stage.id));
};

const getStageOverridesForClient = (client) => STAGES.map((stage) => ({
  ...stage,
  title: stage.id === 'contract' ? getContractStageTitle(client) : stage.title
}));

const getClientTimelineStages = (client, useDefaultDurations = false) => buildTimelineStages(
  client?.kickoffDate,
  useDefaultDurations ? {} : client?.stageDurations,
  useDefaultDurations ? {} : client?.stageStatuses,
  getStageOverridesForClient(client)
);

const getSelectedMaterialsForClient = (client) => MATERIAL_ITEMS.filter((item) => client?.materials?.[item.id]);

const getRiskMetaForClient = (client) => {
  const timelineStages = getClientTimelineStages(client);
  const baselineStages = getClientTimelineStages(client, true);
  const today = toStartOfDay(new Date());
  const selectedMaterials = getSelectedMaterialsForClient(client);
  const missingMaterials = selectedMaterials.filter((item) => !client?.materialsReceived?.[item.id]);
  const routeMaterialsStage = timelineStages.find((stage) => stage.id === 'routeMaterials');
  const materialsReminderDue = Boolean(routeMaterialsStage && today > routeMaterialsStage.endDate);
  const materialsReminderPending = materialsReminderDue && missingMaterials.length > 0;
  const blockedStages = timelineStages.filter((stage) => stage.status === 'blocked');
  const overdueStages = timelineStages.filter((stage) => stage.endDate < today && stage.status !== 'done');
  const currentEndDate = timelineStages[timelineStages.length - 1]?.endDate || today;
  const baselineEndDate = baselineStages[baselineStages.length - 1]?.endDate || currentEndDate;
  const slippageBusinessDays = getBusinessDayDelta(baselineEndDate, currentEndDate);
  const launchChecklistIncomplete = LAUNCH_ITEMS.filter((item) => !client?.launchChecklist?.[item.id]).length;
  const daysUntilLaunch = Math.floor((toStartOfDay(currentEndDate).getTime() - today.getTime()) / 86400000);
  const launchRisk = daysUntilLaunch <= 7 && launchChecklistIncomplete > 0;

  let risk = 'onTrack';
  if (blockedStages.length > 0) {
    risk = 'blocked';
  } else if (overdueStages.length > 0 || materialsReminderPending || slippageBusinessDays > 0 || launchRisk) {
    risk = 'atRisk';
  }

  const actionableStage = blockedStages[0]
    || overdueStages.sort((a, b) => a.endDate - b.endDate)[0]
    || timelineStages.find((stage) => stage.status !== 'done');

  let pendingOwner = actionableStage ? actionableStage.ownerType : null;
  let pendingAction = actionableStage ? actionableStage.title : 'All clear';

  if (materialsReminderPending) {
    pendingOwner = 'client';
    pendingAction = missingMaterials.length === 1 ? '1 material missing' : `${missingMaterials.length} materials missing`;
  } else if (!actionableStage && launchChecklistIncomplete > 0) {
    pendingOwner = 'both';
    pendingAction = 'Launch checklist';
  }

  const clientReminderCount = [
    materialsReminderPending ? 1 : 0,
    overdueStages.some((stage) => stage.ownerType === 'client' || stage.ownerType === 'both') ? 1 : 0
  ].reduce((sum, value) => sum + value, 0);

  return {
    risk,
    timelineStages,
    baselineStages,
    missingMaterials,
    blockedStages,
    overdueStages,
    slippageBusinessDays,
    estimatedLaunchDate: timelineStages.find((stage) => stage.id === 'translations')?.endDate || currentEndDate,
    launchChecklistIncomplete,
    pendingOwner,
    pendingAction,
    clientReminderCount
  };
};

const createClient = (name = 'New Client', currentUser = null) => ({
  id: doc(collection(db, ONBOARDING_COLLECTION)).id,
  name,
  guideName: '',
  mediaPackageWorkspaceId: '',
  baseLanguage: 'pt-PT',
  projectType: PROJECT_TYPES[0].value,
  reviewDocUrl: '',
  meetingLink: DEFAULT_BOOKING_LINK,
  documentsUrl: '',
  projectManagerName: '',
  projectManagerEmail: '',
  projectManagerWhatsapp: '',
  projectManagerAdditionalContacts: '',
  clientContactName: '',
  clientContactEmail: '',
  clientContactWhatsapp: '',
  clientAdditionalContacts: '',
  paymentStatus: 'pending',
  paymentComplete: false,
  paymentNotes: '',
  agreedLaunchDate: '',
  launchCommunicationStartDate: '',
  guideOnlineDate: '',
  guidePublicReadyDate: '',
  guideQrImageUrl: '',
  qrFilesUrl: '',
  guideLanguages: GUIDE_LANGUAGE_OPTIONS.reduce((acc, option) => {
    acc[option.value] = {
      selected: option.value === 'pt-PT',
      ready: option.value === 'pt-PT'
    };
    return acc;
  }, {}),
  kickoffEmailCc: '',
  kickoffDate: toDateInputValue(new Date()),
  activeStageId: STAGES[0].id,
  stageDurations: STAGES.reduce((acc, stage) => {
    acc[stage.id] = stage.defaultDurationDays;
    return acc;
  }, {}),
  stageStatuses: STAGES.reduce((acc, stage, index) => {
    acc[stage.id] = index === 0 ? 'active' : 'todo';
    return acc;
  }, {}),
  materials: MATERIAL_ITEMS.reduce((acc, item) => {
    acc[item.id] = false;
    return acc;
  }, {}),
  materialsReceived: MATERIAL_ITEMS.reduce((acc, item) => {
    acc[item.id] = false;
    return acc;
  }, {}),
  driveUploadUrl: '',
  payments: PAYMENT_ITEMS.reduce((acc, item) => {
    acc[item.id] = false;
    return acc;
  }, {}),
  showContractSteps: true,
  showPaymentSteps: true,
  reviewChecklist: REVIEW_ITEMS.reduce((acc, item) => {
    acc[item.id] = false;
    return acc;
  }, {}),
  launchChecklist: LAUNCH_ITEMS.reduce((acc, item) => {
    acc[item.id] = false;
    return acc;
  }, {}),
  launchResponsibilities: LAUNCH_RESPONSIBILITIES.reduce((acc, item) => {
    acc[item.id] = { owner: item.owner, person: '', evidenceUrl: '', completed: false };
    return acc;
  }, {}),
  scope: {
    product: 'essential',
    includedLanguages: '',
    stops: '',
    words: '',
    features: 'QR code recognition; GPS recognition; Human QA and client approval.',
    addOns: '',
    featureSelection: {},
    included: '',
    excluded: '',
    revisionWindow: 'One consolidated revision round after the first draft.',
    approvalDefinition: 'Approval confirms route order, names, dates, factual accuracy and institutional wording.',
    changeRequestPolicy: 'Requests after approval are reviewed and scheduled separately before production continues.',
    approvedAt: '',
    approvedBy: ''
  },
  postLaunch: { ...DEFAULT_POST_LAUNCH },
  launchPackage: { ...DEFAULT_LAUNCH_PACKAGE },
  promotionPlan: createDefaultPromotionPlan(),
  launchAssets: LAUNCH_ASSET_ITEMS.reduce((acc, item) => {
    acc[item.id] = '';
    return acc;
  }, {}),
  addOns: ADD_ONS.reduce((acc, item) => {
    acc[item.id] = false;
    return acc;
  }, {}),
  reminders: {},
  qrPlacements: QR_PLACEMENTS.reduce((acc, item) => {
    acc[item.id] = false;
    return acc;
  }, {}),
  accessToken: generateAccessToken(),
  publicAccess: true,
  notes: '',
  createdBy: currentUser?.uid || null,
  createdByEmail: currentUser?.email || '',
  createdAt: new Date().toISOString()
});

const ClientOnboardingHub = () => {
  const { currentUser } = useAuth();
  const [clients, setClients] = useState([]);
  const [mediaPackages, setMediaPackages] = useState([]);
  const [activeClientId, setActiveClientId] = useState('');
  const [newClientName, setNewClientName] = useState('');
  const [isLoadingClients, setIsLoadingClients] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saveStatus, setSaveStatus] = useState('Saved');
  const [adminView, setAdminView] = useState('portfolio');
  const [activeHubSection, setActiveHubSection] = useState('timeline');
  const [promotionPhase, setPromotionPhase] = useState('before');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [showRegenerateConfirmation, setShowRegenerateConfirmation] = useState(false);
  const [regenerateConfirmationText, setRegenerateConfirmationText] = useState('');
  const [emailStatus, setEmailStatus] = useState('');
  const [emailStatusType, setEmailStatusType] = useState('info');
  const [emailSendingTarget, setEmailSendingTarget] = useState('');
  const [emailPreviews, setEmailPreviews] = useState({});
  const [emailPreviewLoading, setEmailPreviewLoading] = useState('');
  const [pendingEmailTarget, setPendingEmailTarget] = useState('');
  const [sharedMediaPress, setSharedMediaPress] = useState(createDefaultMediaPress());
  const [activityLog, setActivityLog] = useState([]);
  const [collapsedPanels, setCollapsedPanels] = useState(DEFAULT_COLLAPSED_PANELS);
  const activityTimeoutRef = useRef(null);
  const guideQrUploadRef = useRef(null);

  const activeClient = useMemo(
    () => clients.find((client) => client.id === activeClientId) || clients[0],
    [activeClientId, clients]
  );

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, ONBOARDING_COLLECTION),
      (snapshot) => {
        const nextClients = snapshot.docs
          .map((snapshotDoc) => ({
            ...snapshotDoc.data(),
            id: snapshotDoc.id
          }))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

        setClients(nextClients);
        setIsLoadingClients(false);
        setLoadError('');

        setActiveClientId((currentId) => currentId || nextClients[0]?.id || '');
      },
      (error) => {
        console.error('Failed to load onboarding clients:', error);
        setLoadError(error.message || 'Failed to load onboarding clients.');
        setIsLoadingClients(false);
      }
    );

    return unsubscribe;
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'media_packages'),
      (snapshot) => {
        setMediaPackages(snapshot.docs.map((snapshotDoc) => ({
          workspaceId: snapshotDoc.id,
          ...snapshotDoc.data(),
        })).sort((a, b) => (
          String(a.packageData?.config?.guide_title || a.guideKey || '')
            .localeCompare(String(b.packageData?.config?.guide_title || b.guideKey || ''))
        )));
      },
      (error) => console.error('Failed to load Media Packages for onboarding:', error)
    );
    return unsubscribe;
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, ONBOARDING_SHARED_COLLECTION, MEDIA_PRESS_SHARED_DOC),
      (snapshot) => {
        const data = snapshot.data() || {};
        setSharedMediaPress({
          ...createDefaultMediaPress(),
          ...(data.mediaPress || {})
        });
      },
      (error) => {
        console.error('Failed to load shared onboarding media/press:', error);
        setLoadError(error.message || 'Failed to load shared onboarding media/press.');
      }
    );

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!activeClient?.id) {
      setActivityLog([]);
      return undefined;
    }

    const activityQuery = query(
      collection(db, ONBOARDING_COLLECTION, activeClient.id, 'activity'),
      orderBy('createdAt', 'desc'),
      limit(ACTIVITY_LOG_LIMIT)
    );

    const unsubscribe = onSnapshot(
      activityQuery,
      (snapshot) => {
        setActivityLog(snapshot.docs.map((snapshotDoc) => ({
          id: snapshotDoc.id,
          ...snapshotDoc.data()
        })));
      },
      (error) => {
        console.error('Failed to load onboarding activity log:', error);
      }
    );

    return unsubscribe;
  }, [activeClient?.id]);

  useEffect(() => () => {
    if (activityTimeoutRef.current) {
      window.clearTimeout(activityTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (!activeClient && clients[0]) {
      setActiveClientId(clients[0].id);
    }
  }, [activeClient, clients]);

  useEffect(() => {
    if (activeClient && !activeClient.accessToken) {
      updateActiveClient({ accessToken: generateAccessToken() });
    }
  }, [activeClient]);

  const timelineStages = useMemo(() => {
    if (!activeClient) return [];
    return getClientTimelineStages(activeClient);
  }, [activeClient]);

  const activeRiskMeta = useMemo(() => (
    activeClient ? getRiskMetaForClient(activeClient) : null
  ), [activeClient]);

  const matchedMediaPackage = useMemo(() => {
    const guideName = String(activeClient?.guideName || '').trim().toLocaleLowerCase();
    if (!guideName) return null;
    const matches = mediaPackages.filter((item) => {
      const packageTitle = String(
        item.packageData?.config?.guide_title || item.packageData?.guide?.guide_title || ''
      ).trim().toLocaleLowerCase();
      const packageLanguage = String(item.language || item.packageData?.config?.output_language || '');
      return packageTitle === guideName
        && (!activeClient?.baseLanguage || !packageLanguage || packageLanguage === activeClient.baseLanguage);
    });
    return matches.length === 1 ? matches[0] : null;
  }, [activeClient?.baseLanguage, activeClient?.guideName, mediaPackages]);

  const activeMediaPackage = useMemo(() => (
    mediaPackages.find((item) => item.workspaceId === activeClient?.mediaPackageWorkspaceId)
      || matchedMediaPackage
      || null
  ), [activeClient?.mediaPackageWorkspaceId, matchedMediaPackage, mediaPackages]);

  useEffect(() => {
    if (!activeClient?.id || activeClient.mediaPackageWorkspaceId || !matchedMediaPackage?.workspaceId) return;
    updateActiveClient(
      { mediaPackageWorkspaceId: matchedMediaPackage.workspaceId },
      'Automatically linked matching Media Package to onboarding project'
    );
    // The exact title/language match is the only automatic association path.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClient?.id, activeClient?.mediaPackageWorkspaceId, matchedMediaPackage?.workspaceId]);

  const clientDashboardRows = useMemo(() => clients.map((client) => {
    const riskMeta = getRiskMetaForClient(client);
    const selectedMaterials = getSelectedMaterialsForClient(client);
    return {
      id: client.id,
      name: client.name || 'Untitled client',
      guideName: client.guideName || '',
      risk: riskMeta.risk,
      pendingOwner: riskMeta.pendingOwner,
      pendingAction: riskMeta.pendingAction,
      estimatedLaunchDate: riskMeta.estimatedLaunchDate,
      slippageBusinessDays: riskMeta.slippageBusinessDays,
      reminderCount: riskMeta.clientReminderCount,
      missingMaterialsCount: riskMeta.missingMaterials.length,
      selectedMaterialsCount: selectedMaterials.length,
      activePhase: riskMeta.timelineStages.find((stage) => stage.id === client.activeStageId)?.title || riskMeta.timelineStages[0]?.title || 'No active phase'
    };
  }), [clients]);

  const dashboardSummary = useMemo(() => ({
    total: clientDashboardRows.length,
    onTrack: clientDashboardRows.filter((row) => row.risk === 'onTrack').length,
    atRisk: clientDashboardRows.filter((row) => row.risk === 'atRisk').length,
    blocked: clientDashboardRows.filter((row) => row.risk === 'blocked').length,
    reminders: clientDashboardRows.reduce((sum, row) => sum + row.reminderCount, 0)
  }), [clientDashboardRows]);

  const timelineSpanDays = useMemo(() => {
    if (timelineStages.length === 0) return 0;
    return diffDays(timelineStages[0].startDate, timelineStages[timelineStages.length - 1].endDate) + 1;
  }, [timelineStages]);

  const timelineWorkingDays = useMemo(() => {
    if (timelineStages.length === 0) return 0;
    return countBusinessDaysInclusive(timelineStages[0].startDate, timelineStages[timelineStages.length - 1].endDate);
  }, [timelineStages]);

  const monthSegments = useMemo(() => {
    if (timelineStages.length === 0 || timelineSpanDays <= 0) return [];

    const timelineStart = timelineStages[0].startDate;
    const timelineEnd = timelineStages[timelineStages.length - 1].endDate;
    const segments = [];
    let cursor = new Date(timelineStart.getFullYear(), timelineStart.getMonth(), 1);

    while (cursor <= timelineEnd) {
      const nextMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
      const segmentStart = cursor < timelineStart ? timelineStart : cursor;
      const monthEnd = addDays(nextMonth, -1);
      const segmentEnd = monthEnd > timelineEnd ? timelineEnd : monthEnd;

      segments.push({
        label: new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric' }).format(cursor),
        left: (diffDays(timelineStart, segmentStart) / timelineSpanDays) * 100,
        width: ((diffDays(segmentStart, segmentEnd) + 1) / timelineSpanDays) * 100
      });

      cursor = nextMonth;
    }

    return segments;
  }, [activeClient?.kickoffDate, activeClient?.stageDurations, timelineSpanDays, timelineStages]);

  const weekendSegments = useMemo(() => {
    if (timelineStages.length === 0 || timelineSpanDays <= 0) return [];

    const timelineStart = toStartOfDay(timelineStages[0].startDate);
    const timelineEnd = toStartOfDay(timelineStages[timelineStages.length - 1].endDate);
    const segments = [];
    let cursor = new Date(timelineStart);

    while (cursor <= timelineEnd) {
      if (!isWeekend(cursor)) {
        cursor = addDays(cursor, 1);
        continue;
      }

      const segmentStart = new Date(cursor);
      while (cursor <= timelineEnd && isWeekend(cursor)) {
        cursor = addDays(cursor, 1);
      }
      const segmentEnd = addDays(cursor, -1);

      segments.push({
        left: (diffDays(timelineStart, segmentStart) / timelineSpanDays) * 100,
        width: ((diffDays(segmentStart, segmentEnd) + 1) / timelineSpanDays) * 100
      });
    }

    return segments;
  }, [timelineSpanDays, timelineStages]);

  const todayLineLeft = useMemo(() => {
    if (timelineStages.length === 0 || timelineSpanDays <= 0) return null;

    const today = new Date();
    const normalizedToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const timelineStart = timelineStages[0].startDate;
    const timelineEnd = timelineStages[timelineStages.length - 1].endDate;

    if (normalizedToday < timelineStart || normalizedToday > timelineEnd) {
      return null;
    }

    return (clamp(diffDays(timelineStart, normalizedToday), 0, timelineSpanDays - 1) / timelineSpanDays) * 100;
  }, [timelineSpanDays, timelineStages]);

  const timelineMarkers = useMemo(() => {
    if (timelineStages.length === 0 || timelineSpanDays <= 0) return [];
    const timelineStart = timelineStages[0].startDate;
    const timelineEnd = timelineStages[timelineStages.length - 1].endDate;
    const stageById = Object.fromEntries(timelineStages.map((stage) => [stage.id, stage]));
    return [
      { id: 'launchCommunicationStartDate', date: stageById.launch?.startDate, label: 'Communication starts', color: '#e5a3e8' },
      { id: 'guideOnlineDate', date: stageById.draft?.endDate, label: 'Guide v1 online', color: '#9ddcf0', atEnd: true },
      { id: 'guidePublicReadyDate', date: stageById.translations?.endDate, label: 'Full guide ready', color: '#1687e8', atEnd: true }
    ].map((marker) => {
      if (!marker.date) return null;
      const date = toStartOfDay(marker.date instanceof Date ? marker.date : new Date(`${marker.date}T00:00:00`));
      const positionDate = marker.atEnd ? addDays(date, 1) : date;
      if (date < timelineStart || date > timelineEnd) return null;
      return { ...marker, left: (clamp(diffDays(timelineStart, positionDate), 0, timelineSpanDays) / timelineSpanDays) * 100 };
    }).filter(Boolean);
  }, [activeClient?.kickoffDate, activeClient?.stageDurations, timelineSpanDays, timelineStages]);

  const activeStage = timelineStages.find((stage) => stage.id === activeClient?.activeStageId) || timelineStages[0];
  const activeMediaPressEntries = MEDIA_PRESS_ITEMS.map((item) => {
    const value = sharedMediaPress?.[item.id] || '';
    const links = item.id === 'pressCoverageLinks'
      ? value.split(/\r?\n/).map(parseLabeledLink).filter(Boolean)
      : value.trim() ? [parseLabeledLink(value)].filter(Boolean) : [];

    return {
      ...item,
      value,
      links
    };
  });

  const saveSharedMediaPress = async (updates) => {
    const nextMediaPress = {
      ...sharedMediaPress,
      ...updates
    };

    setSharedMediaPress(nextMediaPress);
    setSaveStatus('Saving shared media / press...');

    try {
      await setDoc(
        doc(db, ONBOARDING_SHARED_COLLECTION, MEDIA_PRESS_SHARED_DOC),
        {
          mediaPress: nextMediaPress,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.uid || null,
          updatedByEmail: currentUser?.email || ''
        },
        { merge: true }
      );
      setSaveStatus('Saved');
    } catch (error) {
      console.error('Failed to save shared onboarding media/press:', error);
      setSaveStatus('Save failed');
      setLoadError(error.message || 'Failed to save shared onboarding media/press.');
    }
  };

  const togglePanel = (panelId) => {
    setCollapsedPanels((current) => ({
      ...current,
      [panelId]: !current[panelId]
    }));
  };

  const openPanel = (panelId) => {
    setCollapsedPanels((current) => ({
      ...current,
      [panelId]: false
    }));
  };

  const addActivityEntry = async (clientId, message, type = 'update', meta = {}) => {
    if (!clientId || !message) return;

    try {
      await addDoc(collection(db, ONBOARDING_COLLECTION, clientId, 'activity'), {
        type,
        message,
        actorUid: currentUser?.uid || null,
        actorEmail: currentUser?.email || '',
        clientName: activeClient?.name || '',
        ...meta,
        createdAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Failed to write onboarding activity log:', error);
    }
  };

  const scheduleActivityEntry = (clientId, message) => {
    if (!clientId || !message) return;

    if (activityTimeoutRef.current) {
      window.clearTimeout(activityTimeoutRef.current);
    }

    activityTimeoutRef.current = window.setTimeout(() => {
      addActivityEntry(clientId, message, 'update');
      activityTimeoutRef.current = null;
    }, 1500);
  };

  const saveClient = async (clientId, clientData) => {
    setSaveStatus('Saving...');

    try {
      await Promise.all([
        setDoc(doc(db, ONBOARDING_COLLECTION, clientId), {
          ...clientData,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.uid || null,
          updatedByEmail: currentUser?.email || ''
        }, { merge: true }),
        setDoc(
          doc(db, ONBOARDING_PUBLIC_COLLECTION, clientId),
          buildPublicOnboardingJson(clientData),
          { merge: true }
        )
      ]);
      setSaveStatus('Saved');
    } catch (error) {
      console.error('Failed to save onboarding client:', error);
      setSaveStatus('Save failed');
      setLoadError(error.message || 'Failed to save onboarding client.');
    }
  };

  const updateActiveClient = (updates, activityMessage = 'Updated project details') => {
    if (!activeClient) return;

    const nextClient = { ...activeClient, ...updates };

    setClients((current) => current.map((client) => (
      client.id === activeClient.id
        ? nextClient
        : client
    )));
    saveClient(activeClient.id, nextClient);
    scheduleActivityEntry(activeClient.id, activityMessage);
  };

  const exportActiveClientJson = () => {
    if (!activeClient) return;
    const backup = { backupVersion: 1, exportedAt: new Date().toISOString(), client: activeClient };
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(activeClient.name || 'onboarding').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'onboarding'}-backup.json`;
    link.click();
    URL.revokeObjectURL(url);
    setSaveStatus('Local backup exported');
  };

  const importActiveClientJson = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !activeClient) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result || '{}'));
        const importedClient = parsed.client || parsed;
        if (!importedClient || typeof importedClient !== 'object' || Array.isArray(importedClient)) throw new Error('Invalid onboarding JSON');
        const nextClient = { ...activeClient, ...importedClient, id: activeClient.id };
        updateActiveClient(nextClient, 'Imported local onboarding backup');
        setSaveStatus('Backup imported and saved');
      } catch (error) {
        setSaveStatus(`Backup import failed: ${error.message}`);
      }
    };
    reader.readAsText(file);
  };

  const updateNestedClientField = (field, key, value) => {
    if (!activeClient) return;

    updateActiveClient({
      [field]: {
        ...(activeClient[field] || {}),
        [key]: value
      }
    }, getActivityMessageForField(field));
  };

  const handleGuideQrImageUpload = async (file) => {
    if (!activeClient || !file) return;
    if (!file.type?.startsWith('image/')) {
      setLoadError('Please upload an image file for the QR code.');
      return;
    }

    setSaveStatus('Uploading QR image...');
    setLoadError('');

    try {
      const storagePath = `onboarding_qr_codes/${activeClient.id}/guide-qr`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, { contentType: file.type });
      const downloadUrl = await getDownloadURL(storageRef);
      updateActiveClient({ guideQrImageUrl: downloadUrl }, 'Updated guide QR image');
      setSaveStatus('QR image uploaded');
      if (guideQrUploadRef.current) {
        guideQrUploadRef.current.value = '';
      }
    } catch (error) {
      console.error('Failed to upload guide QR image:', error);
      setSaveStatus('QR image upload failed');
      setLoadError(error.message || 'Failed to upload QR image.');
    }
  };

  const handleSyncPublicClient = async () => {
    if (!activeClient) return;

    setSaveStatus('Syncing public page...');
    try {
      await setDoc(
        doc(db, ONBOARDING_PUBLIC_COLLECTION, activeClient.id),
        buildPublicOnboardingJson(activeClient),
        { merge: true }
      );
      await addActivityEntry(activeClient.id, 'Synced public onboarding page', 'system');
      setSaveStatus('Public page synced');
    } catch (error) {
      console.error('Failed to sync public onboarding client:', error);
      setSaveStatus('Sync failed');
      setLoadError(error.message || 'Failed to sync public onboarding page.');
    }
  };

  const handleAddClient = () => {
    const nextClient = createClient(newClientName.trim() || 'New Client', currentUser);
    setClients((current) => [...current, nextClient]);
    setActiveClientId(nextClient.id);
    setAdminView('client');
    setNewClientName('');
    saveClient(nextClient.id, nextClient);
    addActivityEntry(nextClient.id, 'Created onboarding project', 'system');
  };

  const handleDeleteClient = async () => {
    if (!activeClient) return;

    const normalizedConfirmation = deleteConfirmationText.trim().toUpperCase();
    if (normalizedConfirmation !== 'DELETE') {
      setLoadError('Type DELETE to confirm client deletion.');
      return;
    }

    setSaveStatus('Deleting...');
    setLoadError('');

    try {
      await addActivityEntry(activeClient.id, 'Deleted onboarding project', 'system');
      await Promise.all([
        deleteDoc(doc(db, ONBOARDING_COLLECTION, activeClient.id)),
        deleteDoc(doc(db, ONBOARDING_PUBLIC_COLLECTION, activeClient.id))
      ]);

      const remainingClients = clients.filter((client) => client.id !== activeClient.id);
      setClients(remainingClients);
      setActiveClientId(remainingClients[0]?.id || '');
      setShowDeleteConfirmation(false);
      setDeleteConfirmationText('');
      setSaveStatus('Deleted');
    } catch (error) {
      console.error('Failed to delete onboarding client:', error);
      setSaveStatus('Delete failed');
      setLoadError(error.message || 'Failed to delete onboarding client.');
    }
  };

  const handleRegenerateAccessLink = async () => {
    if (!activeClient) return;

    const normalizedConfirmation = regenerateConfirmationText.trim().toUpperCase();
    if (normalizedConfirmation !== 'REGENERATE') {
      setLoadError('Type REGENERATE to confirm link regeneration.');
      return;
    }

    const nextToken = generateAccessToken();
    setLoadError('');
    setShowRegenerateConfirmation(false);
    setRegenerateConfirmationText('');
    updateActiveClient({ accessToken: nextToken }, 'Regenerated client access link');
    await addActivityEntry(activeClient.id, 'Regenerated client access link and invalidated previous link', 'security');
  };

  const requestReminderEmail = async (templateType, dryRun = false) => {
    if (!activeClient || !currentUser) {
      throw new Error('No active client selected.');
    }

    const token = await currentUser.getIdToken(true);
    const response = await fetch(ONBOARDING_SEND_REMINDER_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        clientId: activeClient.id,
        templateType,
        dryRun
      })
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Failed to process reminder email.');
    }

    return payload;
  };

  const getReminderMeta = (templateId) => {
    const policy = getReminderPolicy(templateId);
    const reminderEntry = activeClient?.reminders?.[templateId] || {};
    const sendCount = Number(reminderEntry.sendCount || 0);
    const lastSentAt = reminderEntry.lastSentAt ? new Date(reminderEntry.lastSentAt) : null;
    const requiredGap = policy.gapBusinessDays[Math.min(sendCount, policy.gapBusinessDays.length - 1)] || 0;
    const businessDaysSinceLast = lastSentAt ? countBusinessDaysBetween(lastSentAt, new Date()) : null;
    const maxReached = policy.maxSends !== null && sendCount >= policy.maxSends;
    const gapSatisfied = !lastSentAt || businessDaysSinceLast >= requiredGap;
    const nextEligibleDate = lastSentAt && !gapSatisfied ? addBusinessDays(lastSentAt, requiredGap) : null;

    return {
      sendCount,
      lastSentAt,
      requiredGap,
      maxReached,
      gapSatisfied,
      nextEligibleDate,
      canSendNow: !maxReached && gapSatisfied
    };
  };

  const handlePreviewReminderEmail = async (templateType) => {
    const reminderMeta = getReminderMeta(templateType);
    if (!reminderMeta.canSendNow) {
      setEmailStatus(
        reminderMeta.maxReached
          ? 'Reminder limit reached for this template.'
          : `Next reminder available on ${reminderMeta.nextEligibleDate ? formatDate(reminderMeta.nextEligibleDate) : 'a later date'}.`
      );
      setEmailStatusType('error');
      return;
    }

    setEmailPreviewLoading(templateType);
    setEmailStatus('');
    setEmailStatusType('info');
    setLoadError('');

    try {
      const payload = await requestReminderEmail(templateType, true);
      setEmailPreviews((current) => ({
        ...current,
        [templateType]: payload.preview || null
      }));
      setPendingEmailTarget(templateType);
      setEmailStatus(`Preview ready for ${payload.preview?.to || 'selected recipient'}`);
      setEmailStatusType('info');
    } catch (error) {
      console.error('Failed to preview reminder email:', error);
      setEmailStatus(error.message || 'Failed to preview reminder email.');
      setEmailStatusType('error');
    } finally {
      setEmailPreviewLoading('');
    }
  };

  const handleSendReminderEmail = async (templateType) => {
    const reminderMeta = getReminderMeta(templateType);
    if (!reminderMeta.canSendNow) {
      setEmailStatus(
        reminderMeta.maxReached
          ? 'Reminder limit reached for this template.'
          : `Next reminder available on ${reminderMeta.nextEligibleDate ? formatDate(reminderMeta.nextEligibleDate) : 'a later date'}.`
      );
      setEmailStatusType('error');
      return;
    }

    setEmailSendingTarget(templateType);
    setEmailStatus('');
    setEmailStatusType('info');
    setLoadError('');

    try {
      const payload = await requestReminderEmail(templateType, false);
      setEmailStatus(`Email sent to ${payload.recipient} at ${formatDateTime(new Date())}`);
      setEmailStatusType('success');
      setPendingEmailTarget('');
    } catch (error) {
      console.error('Failed to send reminder email:', error);
      setEmailStatus(error.message || 'Failed to send reminder email.');
      setEmailStatusType('error');
    } finally {
      setEmailSendingTarget('');
    }
  };

  const selectedProjectManagerPreset = activeClient
    ? GUIDE_SOFIA_PROJECT_MANAGERS.find((manager) => (
      manager.name === (activeClient?.projectManagerName || '')
      && manager.email === (activeClient?.projectManagerEmail || '')
      && manager.whatsapp === (activeClient?.projectManagerWhatsapp || '')
    ))?.value || ''
    : '';
  const guideLanguageRows = GUIDE_LANGUAGE_OPTIONS.map((option) => {
    return {
      ...option,
      ...getGuideLanguageState(activeClient, option.value)
    };
  });

  if (isLoadingClients) {
    return (
      <div className="min-h-screen bg-slate-50 p-8 text-sm text-slate-600">
        Loading onboarding clients...
      </div>
    );
  }

  if (!activeClient) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-10 sm:px-6 lg:px-8">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-lg font-semibold text-slate-900">Create the first onboarding page</div>
            <p className="mt-2 text-sm text-slate-600">Onboarding pages are saved in Firestore and shared across sessions.</p>
            <div className="mt-4 flex gap-2">
              <input
                value={newClientName}
                onChange={(event) => setNewClientName(event.target.value)}
                placeholder="Client name"
                className="min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
              />
              <button
                type="button"
                onClick={handleAddClient}
                className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
              >
                <Plus className="h-4 w-4" />
                Add
              </button>
            </div>
            {loadError && <p className="mt-3 text-sm text-red-600">{loadError}</p>}
          </div>
        </div>
      </div>
    );
  }

  const selectedMaterials = getSelectedMaterialsForClient(activeClient);
  const completedMaterials = selectedMaterials.length;
  const receivedMaterials = selectedMaterials.filter((item) => activeClient.materialsReceived?.[item.id]).length;
  const completedReviewItems = REVIEW_ITEMS.filter((item) => activeClient.reviewChecklist?.[item.id]).length;
  const completedLaunchItems = LAUNCH_ITEMS.filter((item) => activeClient.launchChecklist?.[item.id]).length;
  const activeLaunchAssets = LAUNCH_ASSET_ITEMS.filter((item) => Boolean(activeClient.launchAssets?.[item.id]?.trim()));
  const selectedProjectType = PROJECT_TYPES.find((type) => type.value === activeClient.projectType)?.label || 'Project type not set';
  const clientAccessUrl = `${PUBLIC_ONBOARDING_BASE_URL}/onboarding/${activeClient.id}?access=${encodeURIComponent(activeClient.accessToken || '')}`;
  const today = toStartOfDay(new Date());
  const riskLabel = activeRiskMeta?.risk === 'blocked' ? 'Blocked' : activeRiskMeta?.risk === 'atRisk' ? 'At risk' : 'On track';
  const pendingOwnerLabel = activeRiskMeta?.pendingOwner ? OWNER_LABELS[activeRiskMeta.pendingOwner] : 'None';
  const suggestedClientEmailTemplates = EMAIL_TEMPLATES.filter((template) => {
    if (!template.requiresConfirmation) return false;
    if (template.id === 'client-kickoff') return false;

    if (template.id === 'client-review-request') {
      const draftStage = timelineStages.find((stage) => stage.id === 'draft');
      const reviewStage = timelineStages.find((stage) => stage.id === 'review');
      return Boolean(
        draftStage?.status === 'done' &&
        reviewStage?.status !== 'done' &&
        !activeClient.reviewChecklist?.clientGuideText &&
        activeClient.reviewDocUrl &&
        getReminderMeta(template.id).sendCount === 0
      );
    }

    if (template.id === 'client-overdue') {
      return timelineStages.some((stage) => (
        (stage.ownerType === 'client' || stage.ownerType === 'both') &&
        stage.endDate < today &&
        stage.status !== 'done'
      ));
    }

    if (template.id === 'client-materials') {
      const routeMaterialsStage = timelineStages.find((stage) => stage.id === 'routeMaterials');
      const materialsReminderDue = Boolean(routeMaterialsStage && today > routeMaterialsStage.endDate);
      return materialsReminderDue && selectedMaterials.some((item) => !activeClient.materialsReceived?.[item.id]);
    }

    return false;
  });
  const actionableClientEmailTemplates = suggestedClientEmailTemplates.filter((template) => getReminderMeta(template.id).canSendNow);
  const shouldShowClientEmailAlert = actionableClientEmailTemplates.length > 0;
  const visibleActivityLog = activityLog.filter((entry) => entry.type !== 'email-preview');

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-5 px-4 py-6 sm:px-6 lg:px-8">
        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-900">Onboarding admin</div>
              <div className="mt-1 text-sm text-slate-500">
                Portfolio dashboard at the top level. Client setup on a separate page.
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setAdminView('portfolio')}
                className={`rounded-md px-3 py-2 text-sm font-medium ${
                  adminView === 'portfolio'
                    ? 'bg-slate-900 text-white'
                    : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                Portfolio
              </button>
              <button
                type="button"
                onClick={() => setAdminView('client')}
                className={`rounded-md px-3 py-2 text-sm font-medium ${
                  adminView === 'client'
                    ? 'bg-slate-900 text-white'
                    : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                Client setup
              </button>
            </div>
          </div>
        </section>

        {adminView === 'portfolio' ? (
          <>
            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Portfolio dashboard</div>
                  <p className="mt-1 text-sm text-slate-500">
                    Global view across all clients, aggregated alerts and baseline vs current timeline slippage.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-4">
                  <div className="rounded-md border border-slate-200 px-3 py-2">
                    <div className="text-xs font-semibold uppercase text-slate-500">On track</div>
                    <div className="mt-1 text-lg font-semibold text-slate-900">{dashboardSummary.onTrack}</div>
                  </div>
                  <div className="rounded-md border border-slate-200 px-3 py-2">
                    <div className="text-xs font-semibold uppercase text-slate-500">At risk</div>
                    <div className="mt-1 text-lg font-semibold text-slate-900">{dashboardSummary.atRisk}</div>
                  </div>
                  <div className="rounded-md border border-slate-200 px-3 py-2">
                    <div className="text-xs font-semibold uppercase text-slate-500">Blocked</div>
                    <div className="mt-1 text-lg font-semibold text-slate-900">{dashboardSummary.blocked}</div>
                  </div>
                  <div className="rounded-md border border-slate-200 px-3 py-2">
                    <div className="text-xs font-semibold uppercase text-slate-500">Client alerts</div>
                    <div className="mt-1 text-lg font-semibold text-slate-900">{dashboardSummary.reminders}</div>
                  </div>
                </div>
              </div>

              <div className="mt-4 overflow-x-auto">
                <div className="min-w-[980px]">
                  <div className="grid grid-cols-[220px_110px_150px_220px_190px_110px_110px] gap-3 border-b border-slate-200 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <div>Client</div>
                    <div>Risk</div>
                    <div>Pending owner</div>
                    <div>Pending action</div>
                    <div>Estimated go live</div>
                    <div>Slippage</div>
                    <div>Alerts</div>
                  </div>
                  <div className="mt-2 grid gap-2">
                    {clientDashboardRows.map((row) => (
                      <button
                        key={row.id}
                        type="button"
                        onClick={() => {
                          setActiveClientId(row.id);
                          setAdminView('client');
                        }}
                        className={`grid grid-cols-[220px_110px_150px_220px_190px_110px_110px] items-center gap-3 rounded-md border px-3 py-3 text-left text-sm ${
                          row.id === activeClient.id ? 'border-slate-900 bg-slate-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                        }`}
                      >
                        <div>
                          <div className="font-medium text-slate-900">{row.name}</div>
                          <div className="truncate text-xs text-slate-500">{row.guideName || row.activePhase}</div>
                        </div>
                        <div>
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${RISK_CHIP_CLASSES[row.risk]}`}>
                            {row.risk === 'blocked' ? 'Blocked' : row.risk === 'atRisk' ? 'At risk' : 'On track'}
                          </span>
                        </div>
                        <div className="text-slate-700">{row.pendingOwner ? OWNER_LABELS[row.pendingOwner] : 'None'}</div>
                        <div className="truncate text-slate-700">{row.pendingAction}</div>
                        <div className="text-slate-700">
                          {formatDate(row.estimatedLaunchDate)}
                          <div className="truncate text-xs text-slate-500">{row.activePhase}</div>
                        </div>
                        <div className={`${row.slippageBusinessDays > 0 ? 'text-rose-700' : row.slippageBusinessDays < 0 ? 'text-emerald-700' : 'text-slate-700'}`}>
                          {row.slippageBusinessDays > 0
                            ? `+${row.slippageBusinessDays} wd`
                            : row.slippageBusinessDays < 0
                              ? `${row.slippageBusinessDays} wd`
                              : 'On time'}
                        </div>
                        <div className="text-slate-700">
                          {row.reminderCount}
                          <div className="text-xs text-slate-500">{row.missingMaterialsCount}/{row.selectedMaterialsCount} missing</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-900">All onboarding guides</div>
                  <p className="mt-1 text-sm text-slate-500">
                    Create a new onboarding page or open an existing client setup.
                  </p>
                </div>
                <div className="flex gap-2">
                  <input
                    value={newClientName}
                    onChange={(event) => setNewClientName(event.target.value)}
                    placeholder="New client name"
                    className="w-56 rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddClient}
                    className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  >
                    <Plus className="h-4 w-4" />
                    Add
                  </button>
                </div>
              </div>

              <div className="mt-4 grid gap-3">
                {clients.map((client) => (
                  <button
                    key={client.id}
                    type="button"
                    onClick={() => {
                      setActiveClientId(client.id);
                      setAdminView('client');
                    }}
                    className={`flex items-center justify-between rounded-md border px-4 py-3 text-left ${
                      client.id === activeClient.id ? 'border-slate-900 bg-slate-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                  >
                    <div>
                      <div className="font-medium text-slate-900">{client.name || 'Untitled client'}</div>
                      <div className="mt-1 text-sm text-slate-500">{client.guideName || 'No guide name yet'}</div>
                    </div>
                    <div className="text-sm font-medium text-slate-700">Open setup</div>
                  </button>
                ))}
              </div>
            </section>
          </>
        ) : (
          <>
            <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Client setup</div>
                  <div className="mt-1 text-sm text-slate-500">
                    Editing {activeClient.name || 'Untitled client'}.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setAdminView('portfolio')}
                  className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to portfolio
                </button>
              </div>
            </section>

        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="text-sm font-semibold uppercase text-slate-500">Client Onboarding</div>
              <input
                value={activeClient.name}
                onChange={(event) => updateActiveClient({ name: event.target.value })}
                className="mt-1 w-full max-w-xl border-0 bg-transparent p-0 text-2xl font-semibold text-slate-900 outline-none"
              />
              <label className="mt-3 block max-w-xl text-xs font-medium text-slate-500">
                Guide name
                <input
                  value={activeClient.guideName || ''}
                  onChange={(event) => updateActiveClient({ guideName: event.target.value })}
                  placeholder="Example: Cascais Historic Centre Audio Guide"
                  className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
                />
              </label>
              <label className="mt-3 block max-w-xl text-xs font-medium text-slate-500">
                Associated Media Package
                <select
                  value={activeClient.mediaPackageWorkspaceId || ''}
                  onChange={(event) => updateActiveClient({ mediaPackageWorkspaceId: event.target.value }, 'Linked Media Package to onboarding project')}
                  className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
                >
                  <option value="">No Media Package linked</option>
                  {mediaPackages.map((item) => (
                    <option key={item.workspaceId} value={item.workspaceId}>
                      {item.packageData?.config?.guide_title || item.packageData?.guide?.guide_title || item.guideKey || item.workspaceId}
                      {item.language ? ` (${item.language})` : ''}
                    </option>
                  ))}
                </select>
                <span className="mt-1 block text-xs font-normal text-slate-400">The client receives a read-only view and one complete ZIP download.</span>
              </label>
              <label className="mt-3 block max-w-xs text-xs font-medium text-slate-500">
                Base language
                <select
                  value={activeClient.baseLanguage || 'pt-PT'}
                  onChange={(event) => updateActiveClient({
                    baseLanguage: event.target.value,
                    guideLanguages: {
                      ...(activeClient.guideLanguages || {}),
                      [event.target.value]: {
                        selected: true,
                        ready: true
                      }
                    }
                  })}
                  className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
                >
                  {ONBOARDING_LANGUAGE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <div className="mt-1 text-sm text-slate-500">
                {selectedProjectType} | {activeStage?.title || 'No active phase'} | {completedMaterials}/{MATERIAL_ITEMS.length} requests | {receivedMaterials}/{completedMaterials || 0} received
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${RISK_CHIP_CLASSES[activeRiskMeta?.risk || 'onTrack']}`}>
                  {riskLabel}
                </span>
                <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                  Pending owner: {pendingOwnerLabel}
                </span>
                <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                  Pending action: {activeRiskMeta?.pendingAction || 'All clear'}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <select
                value={activeClient.id}
                onChange={(event) => setActiveClientId(event.target.value)}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
              >
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>{client.name || 'Untitled Client'}</option>
                ))}
              </select>
              <div className="flex gap-2">
                <input
                  value={newClientName}
                  onChange={(event) => setNewClientName(event.target.value)}
                  placeholder="New client name"
                  className="w-44 rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                />
                <button
                  type="button"
                  onClick={handleAddClient}
                  className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
                >
                  <Plus className="h-4 w-4" />
                  Add
                </button>
              </div>
            </div>
          </div>
          <div className="mt-4 border-t border-slate-200 pt-4">
            {!showDeleteConfirmation ? (
              <button
                type="button"
                onClick={() => {
                  setShowDeleteConfirmation(true);
                  setDeleteConfirmationText('');
                  setLoadError('');
                }}
                className="inline-flex items-center justify-center rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100"
              >
                Delete client
              </button>
            ) : (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-4">
                <div className="text-sm font-semibold text-rose-800">Delete this client?</div>
                <p className="mt-1 text-sm text-rose-700">
                  This removes the internal onboarding record and the client-facing page. Type <span className="font-semibold">DELETE</span> to confirm.
                </p>
                <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                  <input
                    value={deleteConfirmationText}
                    onChange={(event) => setDeleteConfirmationText(event.target.value)}
                    placeholder="Type DELETE"
                    className="w-full rounded-md border border-rose-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-rose-500 sm:max-w-xs"
                  />
                  <button
                    type="button"
                    onClick={handleDeleteClient}
                    className="inline-flex items-center justify-center rounded-md bg-rose-600 px-3 py-2 text-sm font-medium text-white hover:bg-rose-700"
                  >
                    Confirm delete
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteConfirmation(false);
                      setDeleteConfirmationText('');
                      setLoadError('');
                    }}
                    className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {shouldShowClientEmailAlert && (
          <section className="rounded-lg border-2 border-yellow-500 bg-yellow-300 p-6 shadow-sm">
            <div className="text-lg font-semibold text-slate-950">Client email needs attention</div>
            <p className="mt-2 text-sm text-slate-950">
              There {actionableClientEmailTemplates.length === 1 ? 'is' : 'are'}{' '}
              <span className="font-semibold">{actionableClientEmailTemplates.length}</span>{' '}
              client {actionableClientEmailTemplates.length === 1 ? 'email reminder' : 'email reminders'} ready to review.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {actionableClientEmailTemplates.map((template) => {
                const reminderMeta = getReminderMeta(template.id);
                return (
                  <span key={template.id} className="rounded-md bg-white/70 px-3 py-1 text-sm font-medium text-slate-900">
                    {template.label}
                    {!reminderMeta.canSendNow ? ' (waiting)' : ''}
                  </span>
                );
              })}
            </div>
            <div className="mt-4">
              <button
                type="button"
                onClick={() => openPanel('emailReminders')}
                className="inline-flex items-center justify-center rounded-md bg-slate-950 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
              >
                Open email reminders
              </button>
            </div>
          </section>
        )}

        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <button
            type="button"
            onClick={() => togglePanel('clientAccess')}
            className="flex w-full items-center justify-between gap-3 text-left"
          >
            <div>
              <div className="text-sm font-semibold text-slate-900">Client access</div>
              <div className="mt-1 text-sm text-slate-500">Share and manage the client link.</div>
            </div>
            {collapsedPanels.clientAccess ? <ChevronRight className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
          </button>
          {!collapsedPanels.clientAccess && (
            <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div className="min-w-0 flex-1">
                <div className="mt-1 text-sm text-slate-500">Share this private link with the client. Anyone with the link can access the onboarding page.</div>
                <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  Regenerating the link immediately invalidates any client link you already shared.
                </div>
                <div className="mt-2 grid gap-3 lg:grid-cols-[1fr_auto_auto_auto]">
                <label className="min-w-0 text-xs font-medium text-slate-500">
                  Private link
                  <input
                    value={clientAccessUrl}
                    readOnly
                    className="mt-1 w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none"
                  />
                </label>
                <a
                  href={clientAccessUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Open link
                </a>
                <label className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={activeClient.publicAccess !== false}
                    onChange={(event) => updateActiveClient({ publicAccess: event.target.checked })}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                  Enabled
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setShowRegenerateConfirmation(true);
                    setRegenerateConfirmationText('');
                    setLoadError('');
                  }}
                  className="inline-flex items-center justify-center rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Regenerate link
                </button>
                <button
                  type="button"
                  onClick={handleSyncPublicClient}
                  className="inline-flex items-center justify-center rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Sync public
                </button>
                </div>
                {showRegenerateConfirmation && (
                  <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-4">
                  <div className="text-sm font-semibold text-rose-800">Regenerate client link?</div>
                  <p className="mt-1 text-sm text-rose-700">
                    This will disable any previous client link immediately. Type <span className="font-semibold">REGENERATE</span> to confirm.
                  </p>
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                    <input
                      value={regenerateConfirmationText}
                      onChange={(event) => setRegenerateConfirmationText(event.target.value)}
                      placeholder="Type REGENERATE"
                      className="w-full rounded-md border border-rose-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-rose-500 sm:max-w-xs"
                    />
                    <button
                      type="button"
                      onClick={handleRegenerateAccessLink}
                      className="inline-flex items-center justify-center rounded-md bg-rose-600 px-3 py-2 text-sm font-medium text-white hover:bg-rose-700"
                    >
                      Confirm regenerate
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowRegenerateConfirmation(false);
                        setRegenerateConfirmationText('');
                        setLoadError('');
                      }}
                      className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                  </div>
                  </div>
                )}
                <div className="mt-3 text-xs text-slate-500">
                  Activity log: see the "Activity log" section below the Email reminders block.
                </div>
              </div>
            </div>
          )}
        </section>

        <section className="grid gap-5 lg:grid-cols-3">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <button
              type="button"
              onClick={() => togglePanel('guideSofiaContact')}
              className="flex w-full items-center justify-between gap-3 text-left"
            >
              <div>
                <div className="text-sm font-semibold text-slate-900">GuideSofia project contact</div>
                <p className="mt-1 text-sm text-slate-500">Who is in charge of this project and how to reach them.</p>
              </div>
              {collapsedPanels.guideSofiaContact ? <ChevronRight className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
            </button>
            {!collapsedPanels.guideSofiaContact && <div className="mt-4 grid gap-3">
              <label className="grid gap-1 text-sm">
                <span className="text-xs font-medium uppercase text-slate-500">Quick select</span>
                <select
                  value={selectedProjectManagerPreset}
                  onChange={(event) => {
                    const selectedManager = GUIDE_SOFIA_PROJECT_MANAGERS.find((manager) => manager.value === event.target.value);
                    if (!selectedManager) return;

                    updateActiveClient({
                      projectManagerName: selectedManager.name,
                      projectManagerEmail: selectedManager.email,
                      projectManagerWhatsapp: selectedManager.whatsapp
                    });
                  }}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
                >
                  <option value="">Select project manager</option>
                  {GUIDE_SOFIA_PROJECT_MANAGERS.map((manager) => (
                    <option key={manager.value} value={manager.value}>
                      {manager.label} | {manager.email}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-1 text-sm">
                <span className="text-xs font-medium uppercase text-slate-500">Project manager</span>
                <input
                  value={activeClient.projectManagerName || ''}
                  onChange={(event) => updateActiveClient({ projectManagerName: event.target.value })}
                  placeholder="Name"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
                />
              </label>
              <label className="grid gap-1 text-sm">
                <span className="text-xs font-medium uppercase text-slate-500">Email</span>
                <input
                  value={activeClient.projectManagerEmail || ''}
                  onChange={(event) => updateActiveClient({ projectManagerEmail: event.target.value })}
                  placeholder="name@guidesofia.com"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
                />
              </label>
              <label className="grid gap-1 text-sm">
                <span className="text-xs font-medium uppercase text-slate-500">WhatsApp</span>
                <input
                  value={activeClient.projectManagerWhatsapp || ''}
                  onChange={(event) => updateActiveClient({ projectManagerWhatsapp: event.target.value })}
                  placeholder="+351..."
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
                />
              </label>
              <label className="grid gap-1 text-sm">
                <span className="text-xs font-medium uppercase text-slate-500">Additional contacts</span>
                <textarea
                  value={activeClient.projectManagerAdditionalContacts || ''}
                  onChange={(event) => updateActiveClient({ projectManagerAdditionalContacts: event.target.value })}
                  rows={3}
                  placeholder={ADDITIONAL_CONTACTS_PLACEHOLDER}
                  className="min-h-[84px] w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
                />
              </label>
            </div>}
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <button
              type="button"
              onClick={() => togglePanel('clientContact')}
              className="flex w-full items-center justify-between gap-3 text-left"
            >
              <div>
                <div className="text-sm font-semibold text-slate-900">Client project contact</div>
                <p className="mt-1 text-sm text-slate-500">Main contact person on the client side for approvals and coordination.</p>
              </div>
              {collapsedPanels.clientContact ? <ChevronRight className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
            </button>
            {!collapsedPanels.clientContact && <div className="mt-4 grid gap-3">
              <label className="grid gap-1 text-sm">
                <span className="text-xs font-medium uppercase text-slate-500">Contact name</span>
                <input
                  value={activeClient.clientContactName || ''}
                  onChange={(event) => updateActiveClient({ clientContactName: event.target.value })}
                  placeholder="Name"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
                />
              </label>
              <label className="grid gap-1 text-sm">
                <span className="text-xs font-medium uppercase text-slate-500">Email</span>
                <input
                  value={activeClient.clientContactEmail || ''}
                  onChange={(event) => updateActiveClient({ clientContactEmail: event.target.value })}
                  placeholder="name@client.com"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
                />
              </label>
              <label className="grid gap-1 text-sm">
                <span className="text-xs font-medium uppercase text-slate-500">WhatsApp</span>
                <input
                  value={activeClient.clientContactWhatsapp || ''}
                  onChange={(event) => updateActiveClient({ clientContactWhatsapp: event.target.value })}
                  placeholder="+351..."
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
                />
              </label>
              <label className="grid gap-1 text-sm">
                <span className="text-xs font-medium uppercase text-slate-500">Kick-off CC emails</span>
                <textarea
                  value={activeClient.kickoffEmailCc || ''}
                  onChange={(event) => updateActiveClient({ kickoffEmailCc: event.target.value })}
                  rows={3}
                  placeholder="director@client.com, team@client.com"
                  className="min-h-[84px] w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
                />
              </label>
              <label className="grid gap-1 text-sm">
                <span className="text-xs font-medium uppercase text-slate-500">Additional contacts</span>
                <textarea
                  value={activeClient.clientAdditionalContacts || ''}
                  onChange={(event) => updateActiveClient({ clientAdditionalContacts: event.target.value })}
                  rows={3}
                  placeholder={ADDITIONAL_CONTACTS_PLACEHOLDER}
                  className="min-h-[84px] w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
                />
              </label>
            </div>}
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <button
              type="button"
              onClick={() => togglePanel('bookMeeting')}
              className="flex w-full items-center justify-between gap-3 text-left"
            >
              <div>
                <div className="text-sm font-semibold text-slate-900">Book a meeting</div>
                <p className="mt-1 text-sm text-slate-500">Link the client can use to book a meeting with the GuideSofia team.</p>
              </div>
              {collapsedPanels.bookMeeting ? <ChevronRight className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
            </button>
            {!collapsedPanels.bookMeeting && <div className="mt-4 grid gap-3">
              <label className="grid gap-1 text-sm">
                <span className="text-xs font-medium uppercase text-slate-500">Booking link</span>
                <input
                  value={activeClient.meetingLink || ''}
                  onChange={(event) => updateActiveClient({ meetingLink: event.target.value })}
                  placeholder={DEFAULT_BOOKING_LINK}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
                />
              </label>
              {activeClient.meetingLink && (
                <a
                  href={activeClient.meetingLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
                >
                  Open booking link
                </a>
              )}
            </div>}
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <button
            type="button"
            onClick={() => togglePanel('emailReminders')}
            className="flex w-full items-center justify-between gap-3 text-left"
          >
            <div>
              <div className="text-sm font-semibold text-slate-900">Email reminders</div>
              <p className="mt-1 max-w-3xl text-sm text-slate-500">
                Send the kick-off email manually, and send reminder emails when a phase is overdue or when client materials are still missing.
              </p>
              <div className="mt-2 text-xs text-slate-500">
                Client: {activeClient.clientContactEmail || 'No client email set'} | GuideSofia: {activeClient.projectManagerEmail || activeClient.createdByEmail || 'No internal email set'}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                Base language: {activeClient.baseLanguage || 'pt-PT'}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {shouldShowClientEmailAlert && (
                <span className="rounded-md bg-yellow-300 px-3 py-1 text-xs font-semibold text-slate-950">
                  Attention needed
                </span>
              )}
              {collapsedPanels.emailReminders ? <ChevronRight className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
            </div>
          </button>
          {!collapsedPanels.emailReminders && <div className="mt-4 grid gap-4 lg:grid-cols-2">
            {EMAIL_TEMPLATES.map((template) => {
              const preview = emailPreviews[template.id];
              const isPending = pendingEmailTarget === template.id;
              const isSending = emailSendingTarget === template.id;
              const isLoading = emailPreviewLoading === template.id;
              const reminderMeta = getReminderMeta(template.id);
              const isResend = template.id === 'client-review-request' && reminderMeta.sendCount > 0;

              return (
                <div key={template.id} className="rounded-md border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-semibold text-slate-900">{template.label}</div>
                      {actionableClientEmailTemplates.some((candidate) => candidate.id === template.id) && (
                        <span className="rounded-md bg-yellow-300 px-2 py-0.5 text-[11px] font-semibold text-slate-950">
                          Recommended
                        </span>
                      )}
                    </div>
                    {preview?.to && (
                      <div className="text-right text-xs text-slate-500">
                        <div>{preview.to}</div>
                        {preview.cc ? <div>CC: {preview.cc}</div> : null}
                      </div>
                    )}
                  </div>
                  <div className="mt-3">
                    <div className="mb-3 grid gap-1 text-xs text-slate-500">
                      <div>Sent: {reminderMeta.sendCount}{getReminderPolicy(template.id).maxSends !== null ? ` / ${getReminderPolicy(template.id).maxSends}` : ''}</div>
                      <div>Last reminded: {reminderMeta.lastSentAt ? formatDateTime(reminderMeta.lastSentAt) : 'Never'}</div>
                      {!reminderMeta.canSendNow && (
                        <div className="font-medium text-amber-700">
                          {reminderMeta.maxReached
                            ? 'Manual follow-up recommended. Reminder limit reached.'
                            : `Next eligible send: ${reminderMeta.nextEligibleDate ? formatDate(reminderMeta.nextEligibleDate) : 'Later'}`}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => (
                        template.requiresConfirmation
                          ? handlePreviewReminderEmail(template.id)
                          : handleSendReminderEmail(template.id)
                      )}
                      disabled={isLoading || isSending || !reminderMeta.canSendNow}
                      className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {template.requiresConfirmation
                        ? (isLoading ? 'Loading preview...' : (isResend ? 'Preview send again' : 'Preview template'))
                        : (isSending ? 'Sending...' : 'Send now')}
                    </button>
                  </div>

                  {template.requiresConfirmation && preview ? (
                    <>
                      <div className="mt-3 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800">
                        <span className="font-semibold">Subject:</span> {preview.subject}
                      </div>
                      <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap rounded-md border border-slate-200 bg-white px-3 py-3 text-sm leading-6 text-slate-700">
                        {preview.text}
                      </pre>
                      {isPending && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handleSendReminderEmail(template.id)}
                            disabled={isSending}
                            className="inline-flex items-center justify-center rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {isSending ? 'Sending...' : 'Confirm and send'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setPendingEmailTarget('')}
                            className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </>
                      ) : (
                    <div className="mt-3 text-sm text-slate-500">
                      {template.requiresConfirmation ? 'No preview loaded yet.' : 'This template sends immediately without confirmation.'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>}
          {emailStatus && (
            <div className={`mt-3 rounded-md border px-3 py-2 text-sm ${
              emailStatusType === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : emailStatusType === 'error'
                  ? 'border-rose-200 bg-rose-50 text-rose-800'
                  : 'border-slate-200 bg-slate-50 text-slate-700'
            }`}>
              {emailStatus}
            </div>
          )}
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <button
            type="button"
            onClick={() => togglePanel('activityLog')}
            className="flex w-full items-center justify-between gap-3 text-left"
          >
            <div>
              <div className="text-sm font-semibold text-slate-900">Activity log</div>
              <p className="mt-1 text-sm text-slate-500">Recent emails, syncs and project updates for this client.</p>
            </div>
            {collapsedPanels.activityLog ? <ChevronRight className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
          </button>
          {!collapsedPanels.activityLog && <div className="mt-4 grid gap-2">
            {visibleActivityLog.length > 0 ? (
              visibleActivityLog.map((entry) => {
                const createdAt = entry.createdAt?.toDate ? entry.createdAt.toDate() : null;
                return (
                  <div key={entry.id} className="rounded-md border border-slate-200 px-3 py-3">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <div className="text-sm font-medium text-slate-900">{entry.message || 'Activity recorded'}</div>
                      <div className="text-xs text-slate-500">
                        {createdAt ? formatDateTime(createdAt) : 'Saving...'}
                      </div>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {(entry.actorEmail || 'Unknown user')}
                      {entry.recipient ? ` | ${entry.recipient}` : ''}
                      {entry.templateType ? ` | ${entry.templateType}` : ''}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500">
                No activity recorded yet.
              </div>
            )}
          </div>}
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="grid gap-4 lg:grid-cols-[1.2fr_280px]">
            <div>
              <div className="text-sm font-semibold text-slate-900">Client Onboarding Hub</div>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                This page keeps the project clear after the client says yes: timeline, material requests, review status, launch readiness, QR rollout and responsibilities.
              </p>
              <div className="mt-4 grid gap-3 sm:grid-cols-4">
                <div className="rounded-md border border-slate-200 p-3">
                  <div className="text-xs font-semibold uppercase text-slate-500">Materials</div>
                  <div className="mt-1 text-lg font-semibold text-slate-900">{receivedMaterials}/{completedMaterials || 0}</div>
                  {selectedMaterials.length > 0 && (
                    <div className="mt-1 truncate text-xs text-slate-500">
                      {selectedMaterials.slice(0, 3).map((item) => item.label).join(', ')}
                    </div>
                  )}
                </div>
                <div className="rounded-md border border-slate-200 p-3">
                  <div className="text-xs font-semibold uppercase text-slate-500">Launch</div>
                  <div className="mt-1 text-lg font-semibold text-slate-900">{completedLaunchItems}/{LAUNCH_ITEMS.length}</div>
                </div>
                <div className="rounded-md border border-slate-200 p-3">
                  <div className="text-xs font-semibold uppercase text-slate-500">Guide QR</div>
                  <div className="mt-1 text-lg font-semibold text-slate-900">{activeClient.guideQrImageUrl ? 'Ready' : 'Not set'}</div>
                </div>
                <div className="rounded-md border border-slate-200 p-3">
                  <div className="text-xs font-semibold uppercase text-slate-500">Launch dates</div>
                  <div className="mt-2 grid gap-2">
                    <div>
                      <div className="text-[11px] font-semibold uppercase text-slate-500">Estimated go live</div>
                      <div className="mt-1 text-sm font-semibold text-slate-900">
                        {activeRiskMeta?.estimatedLaunchDate ? formatDate(activeRiskMeta.estimatedLaunchDate) : 'Not set'}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        {activeRiskMeta?.slippageBusinessDays > 0
                          ? `+${activeRiskMeta.slippageBusinessDays} working days vs baseline`
                          : activeRiskMeta?.slippageBusinessDays < 0
                            ? `${activeRiskMeta.slippageBusinessDays} working days vs baseline`
                            : 'On baseline'}
                      </div>
                    </div>
                    <div className="border-t border-slate-200 pt-2">
                      <div className="text-[11px] font-semibold uppercase text-slate-500">Agreed launch date</div>
                      <div className="mt-1 text-sm font-semibold text-slate-900">
                        {activeClient.agreedLaunchDate ? formatDate(new Date(`${activeClient.agreedLaunchDate}T00:00:00`)) : 'Not set'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <label className="text-xs font-medium text-slate-500">
              Project type
              <select
                value={activeClient.projectType || ''}
                onChange={(event) => updateActiveClient({ projectType: event.target.value })}
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
              >
                {PROJECT_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-200 pt-4">
            {HUB_SECTIONS.map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => setActiveHubSection(section.id === 'project-details' ? 'documents' : section.id === 'production' ? 'materials' : section.id === 'launch' ? 'qr-codes' : section.id)}
                className={`rounded-md px-3 py-2 text-sm font-medium ${
                  activeHubSection === section.id || (section.id === 'launch' && LAUNCH_SUBSECTIONS.some((item) => item.id === activeHubSection)) || (section.id === 'project-details' && PROJECT_SUBSECTIONS.some((item) => item.id === activeHubSection)) || (section.id === 'production' && PRODUCTION_SUBSECTIONS.some((item) => item.id === activeHubSection))
                    ? 'bg-slate-900 text-white'
                    : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                {section.label}
              </button>
            ))}
          </div>
          {LAUNCH_SUBSECTIONS.some((item) => item.id === activeHubSection) && (
            <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-200 pt-3">
              {LAUNCH_SUBSECTIONS.map((section) => (
                <button key={section.id} type="button" onClick={() => setActiveHubSection(section.id)} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium ${activeHubSection === section.id ? 'bg-cyan-700 text-white' : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}>
                  {section.icon && React.createElement(section.icon, { className: 'h-3.5 w-3.5' })}
                  {section.label}
                </button>
              ))}
            </div>
          )}
          {PROJECT_SUBSECTIONS.some((item) => item.id === activeHubSection) && (
            <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-200 pt-3">
              {PROJECT_SUBSECTIONS.map((section) => <button key={section.id} type="button" onClick={() => setActiveHubSection(section.id)} className={`rounded-md px-3 py-1.5 text-xs font-medium ${activeHubSection === section.id ? 'bg-slate-700 text-white' : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}>{section.label}</button>)}
            </div>
          )}
          {PRODUCTION_SUBSECTIONS.some((item) => item.id === activeHubSection) && (
            <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-200 pt-3">
              {PRODUCTION_SUBSECTIONS.map((section) => <button key={section.id} type="button" onClick={() => setActiveHubSection(section.id)} className={`rounded-md px-3 py-1.5 text-xs font-medium ${activeHubSection === section.id ? 'bg-slate-700 text-white' : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}>{section.label}</button>)}
            </div>
          )}
        </section>

        {activeHubSection === 'timeline' && (
        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <CalendarDays className="h-4 w-4 text-slate-500" />
                Project Timeline
              </div>
              <div className="mt-1 text-sm text-slate-500">
                {timelineStages.length > 0 ? `${formatDate(timelineStages[0].startDate)} to ${formatDate(timelineStages[timelineStages.length - 1].endDate)} | ${timelineWorkingDays} working days` : 'No stages'}
              </div>
            </div>

            <label className="flex items-center gap-3 text-sm text-slate-700">
              <span className="font-medium">Kickoff</span>
              <input
                type="date"
                value={activeClient.kickoffDate}
                onChange={(event) => updateActiveClient({ kickoffDate: event.target.value })}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-500"
              />
            </label>
            <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              Launch markers are linked to the project phases: communication starts with Launch Communication, Guide v1 online with the end of Audioguide First Draft, and Full guide ready with the end of Translations + Voice Overs.
            </div>
          </div>

          <div className="mt-5 overflow-x-auto overflow-y-visible">
            <div className="min-w-[980px]">
              <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                <div className="text-xs font-semibold uppercase text-slate-500">Gantt chart</div>
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: GUIDE_SOFIA_BAR_COLOR }} />
                    GuideSofia
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: CLIENT_BAR_COLOR }} />
                    Client
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: BOTH_BAR_COLOR }} />
                    Shared
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm bg-slate-200" />
                    Weekend
                  </span>
                  <span className="inline-flex items-center gap-1.5"><span className="h-3 w-0.5 bg-[#e5a3e8]" />Communication</span>
                  <span className="inline-flex items-center gap-1.5"><span className="h-3 w-0.5 bg-[#9ddcf0]" />Guide v1 online</span>
                  <span className="inline-flex items-center gap-1.5"><span className="h-3 w-0.5 bg-[#1687e8]" />Full guide ready</span>
                </div>
              </div>

              <div className="relative mt-3 h-8 rounded-t-md bg-slate-50">
                {weekendSegments.map((segment, index) => (
                  <span
                    key={`header-weekend-${index}`}
                    className="absolute bottom-0 top-0 bg-slate-200/80"
                    style={{ left: `${segment.left}%`, width: `${segment.width}%` }}
                  />
                ))}
                {monthSegments.map((month) => (
                  <div
                    key={month.label}
                    className="absolute top-0 h-full border-l border-slate-200 px-2 py-2 text-xs font-medium text-slate-500"
                    style={{ left: `${month.left}%`, width: `${month.width}%` }}
                  >
                    {month.label}
                  </div>
                ))}
                {todayLineLeft !== null && (
                  <>
                    <span
                      className="absolute bottom-0 top-0 z-0 w-1 -translate-x-1/2 rounded-full bg-yellow-400 shadow-[0_0_0_1px_rgba(255,255,255,0.9)]"
                      style={{ left: `${todayLineLeft}%` }}
                    />
                    <span
                      className="absolute top-1 z-30 -translate-x-1/2 rounded-full bg-slate-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm"
                      style={{ left: `${todayLineLeft}%` }}
                    >
                      Today
                    </span>
                  </>
                )}
                {timelineMarkers.map((marker) => (
                  <React.Fragment key={`marker-header-${marker.id}`}>
                    <span className="absolute bottom-0 top-0 z-50 w-0.5 -translate-x-1/2" style={{ left: `${marker.left}%`, backgroundColor: marker.color }} />
                    <span className="absolute top-1 z-50 -translate-x-1/2 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold text-white shadow-sm" style={{ left: `${marker.left}%`, backgroundColor: marker.color }}>
                      {marker.label} · {formatDate(marker.date)}
                    </span>
                  </React.Fragment>
                ))}
              </div>

              <div className="divide-y divide-slate-100">
                {timelineStages.map((stage) => {
                  const isActive = stage.id === activeClient.activeStageId;
                  const left = (diffDays(timelineStages[0].startDate, stage.startDate) / timelineSpanDays) * 100;
                  const width = ((diffDays(stage.startDate, stage.endDate) + 1) / timelineSpanDays) * 100;

                  return (
                    <div
                      key={stage.id}
                      className={`pt-3 pb-0 ${isActive ? 'bg-slate-50' : ''}`}
                    >
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => updateActiveClient({ activeStageId: stage.id })}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            updateActiveClient({ activeStageId: stage.id });
                          }
                        }}
                        className="relative h-12 w-full rounded-md bg-slate-100 text-left overflow-visible"
                        title={`${stage.title}: ${stage.durationDays} days`}
                      >
                        {weekendSegments.map((segment, index) => (
                          <span
                            key={`${stage.id}-weekend-${index}`}
                            className="absolute bottom-0 top-0 bg-slate-200/80"
                            style={{ left: `${segment.left}%`, width: `${segment.width}%` }}
                          />
                        ))}
                        <span className="absolute left-3 top-1 z-10 flex max-w-[38%] items-center gap-1">
                          <span
                            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-[10px] font-semibold ${
                              isActive ? 'bg-slate-900 text-white' : 'bg-white text-slate-700'
                            }`}
                          >
                            {stage.step}
                          </span>
                          <span className="truncate text-[10px] font-medium leading-none text-slate-900">
                            {stage.title}
                          </span>
                        </span>
                        <span className="absolute right-3 top-1/2 z-10 flex max-w-[calc(100%-1.5rem)] items-center justify-end gap-2 -translate-y-1/2 text-xs text-slate-600">
                          <span className="truncate">{stage.owner}</span>
                          <span className="text-slate-400">|</span>
                          <span className="truncate">{formatDate(stage.startDate)} to {formatDate(stage.endDate)}</span>
                          <span className="text-slate-400">|</span>
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_CHIP_CLASSES[stage.status] || STATUS_CHIP_CLASSES.todo}`}>
                            {STATUS_LABELS[stage.status] || stage.status}
                          </span>
                          <span className="text-slate-400">|</span>
                          <span className="shrink-0">{stage.durationDays}d</span>
                        </span>
                        <span className="absolute bottom-1 left-3 z-50 flex items-center gap-2">
                          <select
                            value={stage.status}
                            onClick={(event) => event.stopPropagation()}
                            onChange={(event) => updateNestedClientField('stageStatuses', stage.id, event.target.value)}
                            className="w-28 rounded-md border border-slate-300 bg-white/95 px-2 py-1 text-[11px] text-slate-900 outline-none focus:border-slate-500"
                            aria-label={`${stage.title} status`}
                          >
                            {STATUS_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                          <input
                            type="number"
                            min="0"
                            value={stage.configuredDurationDays}
                            onClick={(event) => event.stopPropagation()}
                            onChange={(event) => updateNestedClientField('stageDurations', stage.id, Math.max(0, Number(event.target.value) || 0))}
                            className="w-16 rounded-md border border-slate-300 bg-white/95 px-2 py-1 text-[11px] text-slate-900 outline-none focus:border-slate-500"
                            aria-label={`${stage.title} ${stage.id === 'launch' ? 'additional' : ''} duration in days`}
                          />
                        </span>
                        {monthSegments.map((month) => (
                          <span
                            key={`${stage.id}-${month.label}`}
                            className="absolute top-0 h-full border-l border-slate-200"
                            style={{ left: `${month.left}%` }}
                          />
                        ))}
                        {todayLineLeft !== null && (
                          <span
                            className="absolute bottom-0 top-0 z-0 w-1 -translate-x-1/2 rounded-full bg-yellow-400 shadow-[0_0_0_1px_rgba(255,255,255,0.9)]"
                            style={{ left: `${todayLineLeft}%` }}
                          />
                        )}
                        {timelineMarkers.map((marker) => (
                          <span key={`${stage.id}-${marker.id}`} className="absolute bottom-0 top-0 z-50 w-0.5 -translate-x-1/2" style={{ left: `${marker.left}%`, backgroundColor: marker.color }} />
                        ))}
                        <span
                          className="absolute top-5 z-40 h-6 rounded-md px-3 text-[10px] font-semibold leading-6 text-white shadow-sm"
                          style={{
                            left: `${left}%`,
                            width: `${Math.max(width, stage.durationDays === 0 ? 1.2 : width)}%`,
                            minWidth: '44px',
                            backgroundColor: stage.color
                          }}
                        >
                          {stage.durationDays}d
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'materials' && (
        <section className="grid gap-5 lg:grid-cols-[1fr_1fr]">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">What We Need</div>
                <div className="mt-1 text-sm text-slate-500">Keep this list short and actionable.</div>
              </div>
              <div className="text-sm font-medium text-slate-600">{receivedMaterials}/{completedMaterials || 0} received</div>
            </div>
            {activeClient.driveUploadUrl && (
              <a
                href={activeClient.driveUploadUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
              >
                <Upload className="h-4 w-4" />
                Upload files to GuideSofia Drive
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            <div className="mt-4 grid gap-2">
              <div className="grid grid-cols-[1fr_88px_88px] items-center gap-3 px-3 text-xs font-semibold uppercase text-slate-500">
                <span>Item</span>
                <span className="text-center">Need</span>
                <span className="text-center">Have</span>
              </div>
              {MATERIAL_ITEMS.map((item) => {
                const Icon = item.icon;
                const requested = Boolean(activeClient.materials?.[item.id]);
                const received = Boolean(activeClient.materialsReceived?.[item.id]);

                return (
                  <div
                    key={item.id}
                    className="grid grid-cols-[1fr_88px_88px] items-center gap-3 rounded-md border border-slate-200 px-3 py-3 text-sm hover:bg-slate-50"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <Icon className="h-4 w-4 shrink-0 text-slate-500" />
                      <span className="truncate text-slate-800">{item.label}</span>
                    </div>
                    <label className="flex justify-center">
                      <input
                        type="checkbox"
                        checked={requested}
                        onChange={(event) => updateNestedClientField('materials', item.id, event.target.checked)}
                        className="h-4 w-4 rounded border-slate-300"
                        aria-label={`Request ${item.label}`}
                      />
                    </label>
                    <label className="flex justify-center">
                      <input
                        type="checkbox"
                        checked={received}
                        disabled={!requested}
                        onChange={(event) => updateNestedClientField('materialsReceived', item.id, event.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 disabled:opacity-40"
                        aria-label={`Mark ${item.label} as received`}
                      />
                    </label>
                  </div>
                );
              })}
            </div>
            <label className="mt-4 block text-xs font-medium text-slate-500">
              Google Drive upload folder
              <div className="mt-1 flex gap-2">
                <input
                  value={activeClient.driveUploadUrl || ''}
                  onChange={(event) => updateActiveClient({ driveUploadUrl: event.target.value })}
                  placeholder="https://drive.google.com/..."
                  className="min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
                />
                {activeClient.driveUploadUrl && (
                  <a
                    href={activeClient.driveUploadUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open
                  </a>
                )}
              </div>
            </label>
          </div>

        </section>
        )}

        {activeHubSection === 'languages' && (
        <section className="grid gap-5 lg:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-semibold text-slate-900">Guide languages</div>
            <p className="mt-1 text-sm text-slate-500">
              Choose which languages this guide will have. Mark a language as ready when its content is finished.
            </p>
            <div className="mt-4 grid gap-2">
              {guideLanguageRows.map((languageRow) => (
                <div key={languageRow.value} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 rounded-2xl border border-slate-200 px-3 py-3 text-sm">
                  <div>
                    <div className="font-medium text-slate-900">{languageRow.label}</div>
                    <div className="text-xs text-slate-500">{languageRow.value === activeClient.baseLanguage ? 'Base language' : 'Additional language'}</div>
                  </div>
                  <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-700">
                    <input
                      type="checkbox"
                      checked={languageRow.selected}
                      onChange={(event) => updateNestedClientField('guideLanguages', languageRow.value, {
                        ...(activeClient.guideLanguages?.[languageRow.value] || {}),
                        selected: event.target.checked,
                        ready: event.target.checked ? Boolean(activeClient.guideLanguages?.[languageRow.value]?.ready || languageRow.value === activeClient.baseLanguage) : false
                      })}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                    Selected
                  </label>
                  <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-700">
                    <input
                      type="checkbox"
                      checked={languageRow.ready}
                      disabled={!languageRow.selected}
                      onChange={(event) => updateNestedClientField('guideLanguages', languageRow.value, {
                        ...(activeClient.guideLanguages?.[languageRow.value] || {}),
                        selected: true,
                        ready: event.target.checked
                      })}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                    Ready
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-semibold text-slate-900">Client view</div>
            <p className="mt-1 text-sm text-slate-500">This is what the client will see on the public page.</p>
            <div className="mt-4 grid gap-2">
              {guideLanguageRows.filter((languageRow) => languageRow.selected).length > 0 ? (
                guideLanguageRows.filter((languageRow) => languageRow.selected).map((languageRow) => (
                  <div key={languageRow.value} className="flex items-center gap-3 rounded-2xl border border-slate-200 px-3 py-3 text-sm">
                    {languageRow.ready ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-amber-500" />}
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900">{languageRow.label}</div>
                      <div className="text-xs text-slate-500">{languageRow.value === activeClient.baseLanguage ? 'Base language' : 'Additional language'}</div>
                    </div>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${languageRow.ready ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                      {languageRow.ready ? 'Ready' : 'In progress'}
                    </span>
                  </div>
                ))
              ) : (
                <div className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500">
                  No guide languages selected yet.
                </div>
              )}
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'review' && (
        <section className="grid gap-5 lg:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Review document</div>
                <div className="mt-1 text-sm text-slate-500">Google Docs link for client review, comments and suggested changes to text, order or stops.</div>
              </div>
              <div className="text-sm font-medium text-slate-600">{completedReviewItems}/{REVIEW_ITEMS.length} complete</div>
            </div>
            <div className="mt-4 grid gap-3">
              <label className="grid gap-1 text-sm">
                <span className="text-xs font-medium uppercase text-slate-500">Google Docs link</span>
                <input
                  value={activeClient.reviewDocUrl || ''}
                  onChange={(event) => updateActiveClient({ reviewDocUrl: event.target.value })}
                  placeholder="https://docs.google.com/..."
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
                />
              </label>
              {activeClient.reviewDocUrl && (
                <a
                  href={activeClient.reviewDocUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
                >
                  Open review doc
                </a>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-semibold text-slate-900">Review checklist</div>
            <div className="mt-4 grid gap-5">
              <div>
                <div className="text-xs font-semibold uppercase text-slate-500">Client</div>
                <div className="mt-2 grid gap-2">
                  {REVIEW_ITEMS.filter((item) => item.owner === 'client').map((item) => {
                    const checked = Boolean(activeClient.reviewChecklist?.[item.id]);
                    const isClientTextItem = item.id === 'clientGuideText';
                    return (
                      <label
                        key={item.id}
                        className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-3 text-sm ${
                          checked ? 'border-emerald-200 bg-emerald-50 hover:bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(event) => updateNestedClientField('reviewChecklist', item.id, event.target.checked)}
                          className="h-4 w-4 rounded border-slate-300"
                        />
                        {checked ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-400" />}
                        <span className="text-slate-800">{item.label}</span>
                        {isClientTextItem && checked && (
                          <button
                            type="button"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              updateActiveClient({
                                reviewChecklist: {
                                  ...(activeClient.reviewChecklist || {}),
                                  clientGuideText: false
                                },
                                clientReviewAcknowledgedAt: null
                              }, 'Reset client review confirmation');
                            }}
                            className="ml-auto inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-600 px-3 py-1 text-xs font-semibold text-white hover:bg-rose-700"
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                            Reset
                          </button>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold uppercase text-slate-500">GuideSofia quality control (Humano)</div>
                <div className="mt-2 grid gap-2">
                  {REVIEW_ITEMS.filter((item) => item.owner === 'guidesofia').map((item) => {
                    const checked = Boolean(activeClient.reviewChecklist?.[item.id]);
                    return (
                      <label
                        key={item.id}
                        className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-3 text-sm ${
                          checked ? 'border-emerald-200 bg-emerald-50 hover:bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(event) => updateNestedClientField('reviewChecklist', item.id, event.target.checked)}
                          className="h-4 w-4 rounded border-slate-300"
                        />
                        {checked ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-400" />}
                        <span className="text-slate-800">{item.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'qr-codes' && (
        <section className="grid gap-5 lg:grid-cols-2">
          <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-5 shadow-sm lg:col-span-2">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-lg font-semibold text-slate-900">QR code files</div>
                <div className="mt-1 text-sm text-slate-600">Add the shared Google Drive or Google Docs link containing all final QR code files for the client.</div>
              </div>
              <ScanLine className="h-6 w-6 text-cyan-700" />
            </div>
            <label className="mt-5 grid gap-1 text-sm">
              <span className="text-xs font-medium uppercase text-slate-600">All QR code files link</span>
              <input type="url" value={activeClient.qrFilesUrl || ''} onChange={(event) => updateActiveClient({ qrFilesUrl: event.target.value })} placeholder="https://drive.google.com/..." className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-cyan-600" />
              <span className="text-xs text-slate-500">The client sees one prominent button that opens this shared folder or document.</span>
            </label>
            {activeClient.qrFilesUrl && <a href={activeClient.qrFilesUrl} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 rounded-full bg-slate-950 px-4 py-2 text-sm font-medium text-white"><ExternalLink className="h-4 w-4" />Open shared QR files</a>}
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div><div className="text-sm font-semibold text-slate-900">Guide QR code</div><div className="mt-1 text-sm text-slate-500">Upload the main QR image that opens the complete guide.</div></div>
              <ScanLine className="h-5 w-5 text-slate-500" />
            </div>
            <div className="mt-4 grid gap-4">
              {activeClient.guideQrImageUrl ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <img src={activeClient.guideQrImageUrl} alt="Guide QR code" className="mx-auto max-h-56 rounded-md border border-slate-200 bg-white p-2 shadow-sm" />
                  <div className="mt-3 flex flex-wrap gap-2"><a href={activeClient.guideQrImageUrl} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center rounded-full bg-slate-950 px-4 py-2 text-sm font-medium text-white">Open QR image</a><button type="button" onClick={() => guideQrUploadRef.current?.click()} className="inline-flex items-center justify-center rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700">Replace image</button></div>
                </div>
              ) : <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">No QR image uploaded yet.</div>}
              <input ref={guideQrUploadRef} type="file" accept="image/*" onChange={(event) => { const [file] = event.target.files || []; if (file) handleGuideQrImageUpload(file); }} className="hidden" />
              <button type="button" onClick={() => guideQrUploadRef.current?.click()} className="inline-flex items-center justify-center rounded-full bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700">{activeClient.guideQrImageUrl ? 'Upload new QR image' : 'Upload QR image'}</button>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div><div className="text-sm font-semibold text-slate-900">QR implementation</div><div className="mt-1 text-sm text-slate-500">Select the placements the client should prepare.</div></div>
              <ScanLine className="h-5 w-5 text-slate-500" />
            </div>
            <div className="mt-4 grid gap-2">
              {QR_PLACEMENTS.map((item) => { const checked = Boolean(activeClient.qrPlacements?.[item.id]); return <label key={item.id} className="flex cursor-pointer items-center gap-3 rounded-md border border-slate-200 px-3 py-3 text-sm hover:bg-slate-50"><input type="checkbox" checked={checked} onChange={(event) => updateNestedClientField('qrPlacements', item.id, event.target.checked)} className="h-4 w-4 rounded border-slate-300" />{checked ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-400" />}<span className="text-slate-800">{item.label}</span></label>; })}
            </div>
            <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm leading-6 text-slate-600">Use clear calls to action such as "Scan to start your audio guide" or "Discover this place in your language".</div>
          </div>
        </section>
        )}

        {activeHubSection === 'launch' && (
        <section className="grid gap-5 lg:grid-cols-[1fr_1fr]">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
            <MediaLaunchTimeline
              compact
              items={activeMediaPackage?.packageData?.config?.promotion_calendar}
              launchDate={activeMediaPackage?.packageData?.config?.launch_date || activeClient.agreedLaunchDate}
              language={activeClient.baseLanguage}
              partnerHandle={activeMediaPackage?.packageData?.config?.partner_social_handle || ''}
            />
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Launch checklist</div>
                <div className="mt-1 text-sm text-slate-500">The guide should only go live when these operational pieces are ready.</div>
              </div>
              <Megaphone className="h-5 w-5 text-slate-500" />
            </div>
            <label className="mt-4 grid gap-1 text-sm">
              <span className="text-xs font-medium uppercase text-slate-500">Agreed launch date</span>
              <input
                type="date"
                value={activeClient.agreedLaunchDate || ''}
                onChange={(event) => updateActiveClient({ agreedLaunchDate: event.target.value })}
                className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
              />
            </label>
            <div className="mt-4 grid gap-2">
              {LAUNCH_ITEMS.map((item) => {
                const checked = Boolean(activeClient.launchChecklist?.[item.id]);

                return (
                  <label
                    key={item.id}
                    className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-3 text-sm ${
                      checked ? 'border-emerald-200 bg-emerald-50 hover:bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(event) => updateNestedClientField('launchChecklist', item.id, event.target.checked)}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                    {checked ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-400" />}
                    <span className="text-slate-800">{item.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="hidden">
            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Guide QR code</div>
                  <div className="mt-1 text-sm text-slate-500">Upload the QR image that lets the client open the guide directly.</div>
                </div>
                <ScanLine className="h-5 w-5 text-slate-500" />
              </div>
              <div className="mt-4 grid gap-4">
                {activeClient.guideQrImageUrl ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <img
                      src={activeClient.guideQrImageUrl}
                      alt="Guide QR code"
                      className="mx-auto max-h-56 rounded-md border border-slate-200 bg-white p-2 shadow-sm"
                    />
                    <div className="mt-3 flex flex-wrap gap-2">
                      <a
                        href={activeClient.guideQrImageUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center rounded-full bg-slate-950 px-4 py-2 text-sm font-medium text-white transition hover:bg-[#003E47]"
                      >
                        Open QR image
                      </a>
                      <button
                        type="button"
                        onClick={() => guideQrUploadRef.current?.click()}
                        className="inline-flex items-center justify-center rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                      >
                        Replace image
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
                    No QR image uploaded yet.
                  </div>
                )}
                <input
                  ref={guideQrUploadRef}
                  type="file"
                  accept="image/*"
                  onChange={(event) => {
                    const [file] = event.target.files || [];
                    if (file) handleGuideQrImageUpload(file);
                  }}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => guideQrUploadRef.current?.click()}
                  className="inline-flex items-center justify-center rounded-full bg-cyan-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-cyan-700"
                >
                  {activeClient.guideQrImageUrl ? 'Upload new QR image' : 'Upload QR image'}
                </button>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">QR implementation</div>
                  <div className="mt-1 text-sm text-slate-500">Select the placements the client should prepare.</div>
                </div>
                <ScanLine className="h-5 w-5 text-slate-500" />
              </div>
              <div className="mt-4 grid gap-2">
                {QR_PLACEMENTS.map((item) => {
                  const checked = Boolean(activeClient.qrPlacements?.[item.id]);

                  return (
                    <label
                      key={item.id}
                      className="flex cursor-pointer items-center gap-3 rounded-md border border-slate-200 px-3 py-3 text-sm hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(event) => updateNestedClientField('qrPlacements', item.id, event.target.checked)}
                        className="h-4 w-4 rounded border-slate-300"
                      />
                      {checked ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-400" />}
                      <span className="text-slate-800">{item.label}</span>
                    </label>
                  );
                })}
              </div>
              <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm leading-6 text-slate-600">
                Use clear calls to action such as "Scan to start your audio guide" or "Discover this place in your language".
              </div>
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'payments' && (
        <section className="grid gap-5 lg:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Contract + Payment</div>
                <div className="mt-1 text-sm text-slate-500">Project administration for contract visibility and payment milestones.</div>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={activeClient.showContractSteps !== false} onChange={(event) => updateActiveClient({ showContractSteps: event.target.checked })} className="h-4 w-4 rounded border-slate-300" />
                  Show contract
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={(activeClient.showPaymentSteps ?? activeClient.showPayments ?? true) !== false} onChange={(event) => updateActiveClient({ showPaymentSteps: event.target.checked })} className="h-4 w-4 rounded border-slate-300" />
                  Show payment
                </label>
                <WalletCards className="h-5 w-5 text-slate-500" />
              </div>
            </div>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {PAYMENT_ITEMS.map((item) => {
                const checked = Boolean(activeClient.payments?.[item.id]);
                return (
                  <label key={item.id} className="flex cursor-pointer items-center gap-3 rounded-md border border-slate-200 px-3 py-3 text-sm hover:bg-slate-50">
                    <input type="checkbox" checked={checked} onChange={(event) => updateNestedClientField('payments', item.id, event.target.checked)} className="h-4 w-4 rounded border-slate-300" />
                    {checked ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-400" />}
                    <span className="text-slate-800">{item.label}</span>
                    <span className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium capitalize text-slate-600">{item.type}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Payment details</div>
                <div className="mt-1 text-sm text-slate-500">Bank coordinates, payment status and internal notes.</div>
              </div>
              <WalletCards className="h-5 w-5 text-slate-500" />
            </div>
            <div className="mt-4 grid gap-3">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase text-slate-500">GuideSofia payment data</div>
                <div className="mt-3 grid gap-2 text-sm">
                  <div><span className="font-medium text-slate-700">Entity:</span> {GUIDE_SOFIA_PAYMENT_DETAILS.entity}</div>
                  <div><span className="font-medium text-slate-700">Address:</span> {GUIDE_SOFIA_PAYMENT_DETAILS.addressLine1}</div>
                  <div className="pl-12 text-slate-700">{GUIDE_SOFIA_PAYMENT_DETAILS.addressLine2}</div>
                  <div><span className="font-medium text-slate-700">Tax number:</span> {GUIDE_SOFIA_PAYMENT_DETAILS.taxNumber}</div>
                  <div><span className="font-medium text-slate-700">Email:</span> {GUIDE_SOFIA_PAYMENT_DETAILS.email}</div>
                  <div><span className="font-medium text-slate-700">Bank:</span> {GUIDE_SOFIA_PAYMENT_DETAILS.bankName}</div>
                  <div><span className="font-medium text-slate-700">IBAN:</span> {GUIDE_SOFIA_PAYMENT_DETAILS.iban}</div>
                </div>
              </div>
              <label className="grid gap-1 text-sm">
                <span className="text-xs font-medium uppercase text-slate-500">Payment status</span>
                <select
                  value={activeClient.paymentStatus || 'pending'}
                  onChange={(event) => updateActiveClient({ paymentStatus: event.target.value })}
                  className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
                >
                  <option value="pending">Pending</option>
                  <option value="deposit">Deposit due</option>
                  <option value="partial">Partially paid</option>
                  <option value="paid">Paid</option>
                  <option value="not_applicable">Not applicable</option>
                </select>
              </label>
              <label className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={Boolean(activeClient.paymentComplete)}
                  onChange={(event) => updateActiveClient({ paymentComplete: event.target.checked })}
                  className="h-4 w-4 rounded border-slate-300"
                />
                Payment complete
              </label>
              <label className="grid gap-1 text-sm">
                <span className="text-xs font-medium uppercase text-slate-500">Notes</span>
                <textarea
                  value={activeClient.paymentNotes || ''}
                  onChange={(event) => updateActiveClient({ paymentNotes: event.target.value })}
                  placeholder="Due dates, invoice timing, special instructions..."
                  className="min-h-[96px] rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
                />
              </label>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-semibold text-slate-900">Client view</div>
            <div className="mt-1 text-sm text-slate-500">This is what the client will see on their onboarding page.</div>
            <div className="mt-4 grid gap-3">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase text-slate-500">GuideSofia payment data</div>
                <div className="mt-3 grid gap-2 text-sm">
                  <div><span className="font-medium text-slate-700">Entity:</span> {GUIDE_SOFIA_PAYMENT_DETAILS.entity}</div>
                  <div><span className="font-medium text-slate-700">Address:</span> {GUIDE_SOFIA_PAYMENT_DETAILS.addressLine1}</div>
                  <div className="pl-12 text-slate-700">{GUIDE_SOFIA_PAYMENT_DETAILS.addressLine2}</div>
                  <div><span className="font-medium text-slate-700">Tax number:</span> {GUIDE_SOFIA_PAYMENT_DETAILS.taxNumber}</div>
                  <div><span className="font-medium text-slate-700">Email:</span> {GUIDE_SOFIA_PAYMENT_DETAILS.email}</div>
                  <div><span className="font-medium text-slate-700">Bank:</span> {GUIDE_SOFIA_PAYMENT_DETAILS.bankName}</div>
                  <div><span className="font-medium text-slate-700">IBAN:</span> {GUIDE_SOFIA_PAYMENT_DETAILS.iban}</div>
                </div>
              </div>
              <div className="rounded-md border border-slate-200 p-4">
                <div className="text-xs font-semibold uppercase text-slate-500">Payment status</div>
                <div className="mt-1 text-sm text-slate-800">{activeClient.paymentStatus || 'pending'}</div>
              </div>
              <div className={`rounded-md border p-4 ${activeClient.paymentComplete ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
                <div className="text-xs font-semibold uppercase text-slate-500">Payment complete</div>
                <div className={`mt-1 text-sm font-medium ${activeClient.paymentComplete ? 'text-emerald-800' : 'text-amber-800'}`}>
                  {activeClient.paymentComplete ? 'Yes, payment is complete' : 'No, payment is still open'}
                </div>
              </div>
              <div className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500">
                If this is a pro-bono project, set payment status to “Not applicable”.
              </div>
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'add-ons' && (
        <section className="grid gap-5 lg:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Optional add-ons</div>
                <div className="mt-1 text-sm text-slate-500">Select the offers the client can review and optionally request.</div>
              </div>
              <Sparkles className="h-5 w-5 text-slate-500" />
            </div>
            <div className="mt-4 grid gap-3">
              {ADD_ONS.map((item) => {
                const Icon = item.icon;
                const checked = Boolean(activeClient.addOns?.[item.id]);

                return (
                  <label
                    key={item.id}
                    className={`flex cursor-pointer gap-3 rounded-md border px-4 py-4 text-sm ${
                      checked ? 'border-amber-200 bg-amber-50' : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(event) => updateNestedClientField('addOns', item.id, event.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-slate-300"
                    />
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
                    <div>
                      <div className="font-medium text-slate-900">{item.label}</div>
                      <div className="mt-1 text-slate-600">{item.description}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-semibold text-slate-900">Client view</div>
            <div className="mt-1 text-sm text-slate-500">These are the add-ons the client will see on their onboarding page.</div>
            <div className="mt-4 grid gap-3">
              {ADD_ONS.filter((item) => activeClient.addOns?.[item.id]).length > 0 ? (
                ADD_ONS.filter((item) => activeClient.addOns?.[item.id]).map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.id} className="rounded-md border border-slate-200 px-4 py-4">
                      <div className="flex items-center gap-3">
                        <Icon className="h-4 w-4 text-slate-500" />
                        <div className="font-medium text-slate-900">{item.label}</div>
                      </div>
                      <div className="mt-2 text-sm text-slate-600">{item.description}</div>
                    </div>
                  );
                })
              ) : (
                <div className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500">
                  No optional add-ons selected yet.
                </div>
              )}
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'launch-assets' && (
        <section className="grid gap-5 lg:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Launch assets</div>
                <div className="mt-1 text-sm text-slate-500">Links clients can use when preparing launch communication.</div>
              </div>
              <ScanLine className="h-5 w-5 text-slate-500" />
            </div>
            <div className="mt-4 grid gap-3">
              {LAUNCH_ASSET_ITEMS.map((item) => (
                <label key={item.id} className="grid gap-1 text-sm">
                  <span className="text-xs font-medium uppercase text-slate-500">{item.label}</span>
                  <span className="text-xs text-slate-500">Use one line per item in the format: `Label | https://...`</span>
                  <textarea
                    value={activeClient.launchAssets?.[item.id] || ''}
                    onChange={(event) => updateNestedClientField('launchAssets', item.id, event.target.value)}
                    rows={3}
                    placeholder="Museum QR poster | https://...\nInstagram launch post | https://..."
                    className="min-h-[84px] w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
                  />
                </label>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-semibold text-slate-900">Preview</div>
            <div className="mt-4 grid gap-2">
              {activeLaunchAssets.length > 0 ? (
                activeLaunchAssets.map((item) => {
                  const value = activeClient.launchAssets?.[item.id] || '';
                  const links = value.split(/\r?\n/).map(parseLabeledLink).filter(Boolean);

                  return links.map((link, index) => (
                    <a
                      key={`${item.id}-${index}`}
                      href={link.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3 rounded-md border border-slate-200 px-3 py-3 text-sm text-slate-800 hover:bg-slate-50"
                    >
                      <ExternalLink className="h-4 w-4 text-slate-500" />
                      <span className="truncate">{link.label}</span>
                      <span className="ml-auto truncate text-slate-500">{item.label}</span>
                    </a>
                  ));
                })
              ) : (
                <div className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500">
                  No launch assets added yet.
                </div>
              )}
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'media-press' && (
        <ClientMediaPackageView mediaPackage={activeMediaPackage} language={activeClient.baseLanguage} />
        )}

        {activeHubSection === 'documents' && (
        <section className="grid gap-5 lg:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-semibold text-slate-900">Documents</div>
            <div className="mt-1 text-sm text-slate-500">Shared document area for contracts and other client-facing files.</div>
            <div className="mt-4 grid gap-3">
              <label className="grid gap-1 text-sm">
                <span className="text-xs font-medium uppercase text-slate-500">Documents link</span>
                <input
                  value={activeClient.documentsUrl || ''}
                  onChange={(event) => updateActiveClient({ documentsUrl: event.target.value })}
                  placeholder="https://drive.google.com/..."
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
                />
              </label>
              {activeClient.documentsUrl && (
                <a
                  href={activeClient.documentsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
                >
                  Open documents link
                </a>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-semibold text-slate-900">Client view</div>
            <div className="mt-1 text-sm text-slate-500">This is the documents button the client will see on their onboarding page.</div>
            <div className="mt-4">
              {activeClient.documentsUrl ? (
                <a
                  href={activeClient.documentsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex w-full items-center justify-center rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
                >
                  Open documents folder
                </a>
              ) : (
                <div className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500">
                  Documents link not set yet.
                </div>
              )}
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'scope' && (
        <section className="grid gap-5 lg:grid-cols-2">
          <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-5 shadow-sm lg:col-span-2">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div><div className="text-xs font-semibold uppercase tracking-wide text-cyan-800">Product scope</div><div className="mt-1 text-xl font-semibold text-slate-900">{SCOPE_PRODUCT_PROFILES[activeClient.scope?.product || 'essential'].label}</div><p className="mt-1 text-sm text-slate-700">{SCOPE_PRODUCT_PROFILES[activeClient.scope?.product || 'essential'].idealFor}</p></div>
              <select value={activeClient.scope?.product || 'essential'} onChange={(event) => updateActiveClient({ scope: { ...(activeClient.scope || {}), product: event.target.value, featureSelection: {} } }, 'Updated scope product')} className="rounded-md border border-cyan-300 bg-white px-3 py-2 text-sm text-slate-900"><option value="essential">Essential Guide</option><option value="plus">Plus Guide</option><option value="premium">Premium AI</option></select>
            </div>
            {(() => { const profile = SCOPE_PRODUCT_PROFILES[activeClient.scope?.product || 'essential']; return <div className="mt-4 grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-4"><div><div className="text-xs font-semibold uppercase text-cyan-800">Guide scope</div><div className="mt-1 text-slate-800">{profile.pois} / {profile.words}</div></div><div><div className="text-xs font-semibold uppercase text-cyan-800">Content source</div><div className="mt-1 text-slate-800">{profile.source}</div></div><div><div className="text-xs font-semibold uppercase text-cyan-800">Revisions</div><div className="mt-1 text-slate-800">{profile.revisions}</div></div><div><div className="text-xs font-semibold uppercase text-cyan-800">Languages</div><div className="mt-1 text-slate-800">{profile.languages}</div></div></div>; })()}
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
            <div className="text-sm font-semibold text-slate-900">Scope checklist</div>
            <p className="mt-1 text-sm text-slate-500">The selected product defines what will be built. Use the checkboxes above for included features and add-ons.</p>
            <div className="mt-4 rounded-md border border-slate-200 p-4"><div className="text-xs font-semibold uppercase text-slate-500">Included platform features</div><div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">{SCOPE_FEATURES.map(([id, label]) => { const profile = SCOPE_PRODUCT_PROFILES[activeClient.scope?.product || 'essential']; const checked = activeClient.scope?.featureSelection?.[id] ?? profile.features.includes(id); return <label key={id} className="flex items-center gap-3 rounded-md border border-slate-200 px-3 py-3 text-sm text-slate-800"><input type="checkbox" checked={checked} onChange={(event) => updateNestedClientField('scope', 'featureSelection', { ...(activeClient.scope?.featureSelection || {}), [id]: event.target.checked })} className="h-4 w-4 rounded border-slate-300" />{label}</label>; })}</div></div>
            <div className="mt-4 rounded-md border border-slate-200 p-4"><div className="text-xs font-semibold uppercase text-slate-500">Selected add-ons</div><div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">{ADD_ONS.map((item) => <label key={item.id} className="flex items-center gap-3 rounded-md border border-slate-200 px-3 py-3 text-sm text-slate-800"><input type="checkbox" checked={Boolean(activeClient.addOns?.[item.id])} onChange={(event) => updateNestedClientField('addOns', item.id, event.target.checked)} className="h-4 w-4 rounded border-slate-300" />{item.label}</label>)}</div></div>
            <div className="mt-4 rounded-md border border-slate-200 p-4"><div className="text-xs font-semibold uppercase text-slate-500">Included languages</div><p className="mt-1 text-xs text-slate-500">Managed in Production → Languages.</p><div className="mt-3 flex flex-wrap gap-2">{guideLanguageRows.filter((row) => row.selected).map((row) => <span key={row.value} className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800">{row.label}</span>)}{guideLanguageRows.every((row) => !row.selected) && <span className="text-sm text-slate-500">No languages selected yet.</span>}</div></div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              {[
                ['included', 'Included in this project', 5],
                ['stops', 'Confirmed stops / POIs', 2],
                ['includedLanguages', 'Confirmed languages', 2],
                ['features', 'Included platform features', 3],
                ['addOns', 'Selected add-ons', 3],
                ['excluded', 'Excluded / not included', 5],
                ['revisionWindow', 'Revision window', 3],
                ['approvalDefinition', 'Approval means', 3],
                ['changeRequestPolicy', 'Requests after approval', 3]
              ].filter(([key]) => ['excluded', 'changeRequestPolicy'].includes(key)).map(([key, label, rows]) => (
                <label key={key} className="grid gap-1 text-sm">
                  <span className="text-xs font-medium uppercase text-slate-500">{key === 'excluded' ? 'Specific exclusions or agreements' : 'Requests after approval'}</span>
                  <textarea value={activeClient.scope?.[key] || ''} onChange={(event) => updateNestedClientField('scope', key, event.target.value)} rows={rows} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500" />
                </label>
              ))}
            </div>
            <div className="mt-4 grid gap-3 border-t border-slate-200 pt-4 md:grid-cols-3">
              <label className="grid gap-1 text-sm"><span className="text-xs font-medium uppercase text-slate-500">Approved by</span><input value={activeClient.scope?.approvedBy || ''} onChange={(event) => updateNestedClientField('scope', 'approvedBy', event.target.value)} className="rounded-md border border-slate-300 px-3 py-2" /></label>
              <label className="grid gap-1 text-sm"><span className="text-xs font-medium uppercase text-slate-500">Approval date</span><input type="date" value={activeClient.scope?.approvedAt || ''} onChange={(event) => updateNestedClientField('scope', 'approvedAt', event.target.value)} className="rounded-md border border-slate-300 px-3 py-2" /></label>
              <div className={`rounded-md border p-3 text-sm ${activeClient.scope?.approvedAt ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>{activeClient.scope?.approvedAt ? 'Scope approval recorded' : 'Scope approval still pending'}</div>
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'launch-promotion' && (
        <section className="grid gap-5">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div><div className="flex flex-wrap items-center gap-2"><div className="text-sm font-semibold text-slate-900">Launch promotion plan</div><span className="rounded-full border border-yellow-300 bg-yellow-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-yellow-900">Work in progress</span></div><p className="mt-1 text-sm text-slate-500">Configure the client campaign across the three launch phases. Clients can update status and notes from their public page.</p><div className="mt-3 rounded-md border border-cyan-200 bg-cyan-50 p-3"><div className="text-xs font-semibold uppercase tracking-wide text-cyan-900">{getPromotionPlanNotice(activeClient.baseLanguage).label}</div><p className="mt-1 text-sm leading-6 text-cyan-950">{getPromotionPlanNotice(activeClient.baseLanguage).body}</p></div></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={activeClient.promotionPlan?.enabled !== false} onChange={(event) => updateActiveClient({ promotionPlan: { ...(activeClient.promotionPlan || createDefaultPromotionPlan()), enabled: event.target.checked } }, 'Updated launch promotion plan')} /> Enabled</label>
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            {[['before', 'Before launch'], ['launch-day', 'Launch day'], ['after', 'After launch']].map(([phase, title]) => { const phaseTasks = (activeClient.promotionPlan?.tasks || createDefaultPromotionPlan().tasks).filter((task) => task.phase === phase); const complete = phaseTasks.filter((task) => task.completed).length; return <button key={phase} type="button" onClick={() => setPromotionPhase(phase)} className={`rounded-2xl border p-3 text-left ${promotionPhase === phase ? 'border-cyan-600 bg-cyan-50' : 'border-slate-200 bg-white'}`}><div className="text-sm font-semibold text-slate-900">{title}</div><div className="mt-1 text-xs text-slate-500">{complete} of {phaseTasks.length} complete</div></button>; })}
          </div>
          {promotionPhase === 'before' && <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-4"><div className="text-sm font-semibold text-cyan-950">Tell all relevant teams about the launch</div><p className="mt-1 text-sm leading-6 text-cyan-900">Use the staff training to prepare reception and visitor-facing teams before launch day.</p><button type="button" onClick={() => setActiveHubSection('launch-training')} className="mt-3 rounded-lg bg-cyan-700 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-800">Open Client launch training</button></div>}
          <div className="mt-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm"><div className="text-sm font-semibold text-slate-900">{promotionPhase === 'before' ? 'Before launch' : promotionPhase === 'launch-day' ? 'Launch day' : 'After launch'}</div><div className="mt-4 grid gap-2">{(activeClient.promotionPlan?.tasks || createDefaultPromotionPlan().tasks).filter((task) => task.phase === promotionPhase).map((rawTask) => { const task = localizePromotionTask(rawTask, activeClient.baseLanguage); return <div key={task.id} className={`rounded-md border p-4 ${task.completed ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200'}`}><div className="flex items-center gap-3"><CheckCircle2 className={`h-4 w-4 ${task.completed ? 'text-emerald-600' : 'text-slate-300'}`} /><div className="font-medium text-slate-900">{task.title}</div></div><p className="mt-2 text-sm leading-6 text-slate-600">{task.guidance}</p>{task.example && <div className="mt-2 rounded-md bg-slate-50 p-3 text-sm italic text-slate-700">{resolvePromotionCopy(task.example, activeClient)}</div>}<div className="mt-2 text-xs text-slate-500">Client status: {task.completed ? 'Completed' : 'Not completed'}</div></div>; })}</div></div>
        </section>
        )}

        {activeHubSection === 'launch-training' && (
        <section className="grid gap-5 lg:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
            <div className="text-sm font-semibold text-slate-900">Client launch training</div>
            <p className="mt-1 text-sm leading-6 text-slate-600">This is the guidance clients see before launch. It explains how to make the guide visible, help staff recommend it and test the visitor experience.</p>
            {activeClient.baseLanguage === 'pt-PT' && <StaffTrainingGuide steps={getStaffTrainingSteps(activeClient.baseLanguage)} institutionName={activeClient.name} guideName={activeClient.guideName} qrImageUrl={activeClient.guideQrImageUrl} />}
          </div>
          {getLaunchTrainingSteps(activeClient.baseLanguage).map(([title, body]) => <div key={title} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm"><div className="text-sm font-semibold text-slate-900">{title}</div>{body.split(' | ').filter((part) => !/^(DONE|CONCLUÍDO):/.test(part)).map((part) => { const [label, ...text] = part.split(': '); return <div key={label} className="mt-3"><div className="text-[11px] font-semibold uppercase text-slate-500">{label}</div><p className="mt-1 text-sm leading-6 text-slate-600">{text.join(': ')}</p></div>; })}</div>)}
        </section>
        )}

        {activeHubSection === 'launch-package' && (
        <section className="grid gap-5 lg:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-semibold text-slate-900">Launch package</div>
            <p className="mt-1 text-sm text-slate-500">Choose the support level and schedule the launch activities.</p>
            <div className="mt-4 grid gap-3">
              <label className="grid gap-1 text-sm"><span className="text-xs font-medium uppercase text-slate-500">Package level</span><select value={activeClient.launchPackage?.level || 'supported'} onChange={(event) => updateNestedClientField('launchPackage', 'level', event.target.value)} className="rounded-md border border-slate-300 px-3 py-2"><option value="essential">Essential Launch</option><option value="supported">Supported Launch</option><option value="strategic">Strategic Launch</option></select></label>
              <label className="grid gap-1 text-sm"><span className="text-xs font-medium uppercase text-slate-500">Launch planning call</span><input type="date" value={activeClient.launchPackage?.planningCallDate || ''} onChange={(event) => updateNestedClientField('launchPackage', 'planningCallDate', event.target.value)} className="rounded-md border border-slate-300 px-3 py-2" /></label>
              <label className="grid gap-1 text-sm"><span className="text-xs font-medium uppercase text-slate-500">Staff training</span><input type="date" value={activeClient.launchPackage?.staffTrainingDate || ''} onChange={(event) => updateNestedClientField('launchPackage', 'staffTrainingDate', event.target.value)} className="rounded-md border border-slate-300 px-3 py-2" /></label>
              <div className="grid gap-3 sm:grid-cols-2"><label className="grid gap-1 text-sm"><span className="text-xs font-medium uppercase text-slate-500">Support starts</span><input type="datetime-local" value={activeClient.launchPackage?.supportStart || ''} onChange={(event) => updateNestedClientField('launchPackage', 'supportStart', event.target.value)} className="rounded-md border border-slate-300 px-3 py-2" /></label><label className="grid gap-1 text-sm"><span className="text-xs font-medium uppercase text-slate-500">Support ends</span><input type="datetime-local" value={activeClient.launchPackage?.supportEnd || ''} onChange={(event) => updateNestedClientField('launchPackage', 'supportEnd', event.target.value)} className="rounded-md border border-slate-300 px-3 py-2" /></label></div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-semibold text-slate-900">Client materials</div>
            <p className="mt-1 text-sm text-slate-500">Link the prepared materials so the client can find them from the public hub.</p>
            <div className="mt-4 grid gap-3">
              {[['launchPlanUrl', 'Client launch plan'], ['staffCheatSheetUrl', 'Staff cheat sheet'], ['promotionKitUrl', 'Promotion kit'], ['technicalTestUrl', 'Technical readiness test'], ['supportProtocolUrl', 'Launch-day support protocol'], ['postLaunchReviewUrl', 'Post-launch review']].map(([key, label]) => <label key={key} className="grid gap-1 text-sm"><span className="text-xs font-medium uppercase text-slate-500">{label}</span><input type="url" value={activeClient.launchPackage?.[key] || ''} onChange={(event) => updateNestedClientField('launchPackage', key, event.target.value)} placeholder="https://..." className="rounded-md border border-slate-300 px-3 py-2" /></label>)}
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'launch' && (
        <section className="grid gap-5 lg:grid-cols-2">
          <div className="hidden">
            <div className="text-sm font-semibold text-slate-900">Launch ownership and evidence</div>
            <p className="mt-1 text-sm text-slate-500">Assign the person responsible and capture proof before launch.</p>
            <div className="mt-4 grid gap-3">
              {LAUNCH_RESPONSIBILITIES.map((item) => {
                const value = activeClient.launchResponsibilities?.[item.id] || {};
                return <div key={item.id} className="rounded-md border border-slate-200 p-3">
                  <div className="text-sm font-medium text-slate-900">{item.label}</div>
                  <div className="mt-2 grid gap-2 md:grid-cols-[1fr_1fr_auto]">
                    <input value={value.person || ''} onChange={(event) => updateNestedClientField('launchResponsibilities', item.id, { ...value, person: event.target.value })} placeholder="Named person" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                    <input value={value.evidenceUrl || ''} onChange={(event) => updateNestedClientField('launchResponsibilities', item.id, { ...value, evidenceUrl: event.target.value })} placeholder="Evidence link" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                    <label className="flex items-center gap-2 px-2 text-sm"><input type="checkbox" checked={Boolean(value.completed)} onChange={(event) => updateNestedClientField('launchResponsibilities', item.id, { ...value, completed: event.target.checked })} /> Done</label>
                  </div>
                </div>;
              })}
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-semibold text-slate-900">Post-launch plan</div>
            <div className="mt-4 grid gap-3">
              <label className="grid gap-1 text-sm"><span className="text-xs font-medium uppercase text-slate-500">Follow-up date</span><input type="date" value={activeClient.postLaunch?.followUpDate || ''} onChange={(event) => updateNestedClientField('postLaunch', 'followUpDate', event.target.value)} className="rounded-md border border-slate-300 px-3 py-2" /></label>
              <label className="grid gap-1 text-sm"><span className="text-xs font-medium uppercase text-slate-500">Follow-up status</span><select value={activeClient.postLaunch?.followUpStatus || 'planned'} onChange={(event) => updateNestedClientField('postLaunch', 'followUpStatus', event.target.value)} className="rounded-md border border-slate-300 px-3 py-2"><option value="planned">Planned</option><option value="scheduled">Scheduled</option><option value="complete">Complete</option></select></label>
              <label className="grid gap-1 text-sm"><span className="text-xs font-medium uppercase text-slate-500">Maintenance and support terms</span><textarea rows={5} value={activeClient.postLaunch?.supportTerms || ''} onChange={(event) => updateNestedClientField('postLaunch', 'supportTerms', event.target.value)} className="rounded-md border border-slate-300 px-3 py-2" /></label>
              <label className="grid gap-1 text-sm"><span className="text-xs font-medium uppercase text-slate-500">Follow-up notes</span><textarea rows={3} value={activeClient.postLaunch?.followUpNotes || ''} onChange={(event) => updateNestedClientField('postLaunch', 'followUpNotes', event.target.value)} className="rounded-md border border-slate-300 px-3 py-2" /></label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={Boolean(activeClient.postLaunch?.analyticsReview)} onChange={(event) => updateNestedClientField('postLaunch', 'analyticsReview', event.target.checked)} /> Analytics review included / completed</label>
              <label className="grid gap-1 text-sm"><span className="text-xs font-medium uppercase text-slate-500">Case study permission</span><select value={activeClient.postLaunch?.caseStudyPermission || 'not-asked'} onChange={(event) => updateNestedClientField('postLaunch', 'caseStudyPermission', event.target.value)} className="rounded-md border border-slate-300 px-3 py-2"><option value="not-asked">Not asked</option><option value="yes">Yes</option><option value="no">No</option></select></label>
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'roles' && (
        <section className="grid gap-5 lg:grid-cols-[1fr_1fr]">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-semibold text-slate-900">GuideSofia responsibilities</div>
            <div className="mt-4 grid gap-2">
              {GUIDE_SOFIA_RESPONSIBILITIES.map((item) => (
                <div key={item} className="flex items-center gap-3 rounded-md border border-slate-200 px-3 py-3 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-blue-600" />
                  <span className="text-slate-800">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-semibold text-slate-900">Client responsibilities</div>
            <div className="mt-4 grid gap-2">
              {CLIENT_RESPONSIBILITIES.map((item) => (
                <div key={item} className="flex items-center gap-3 rounded-md border border-slate-200 px-3 py-3 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span className="text-slate-800">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
            <div className="text-sm font-semibold text-slate-900">Review rules</div>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <div className="rounded-md border border-slate-200 p-4">
                <div className="text-sm font-medium text-slate-900">First draft</div>
                <p className="mt-2 text-sm leading-6 text-slate-600">GuideSofia creates the first script from the client materials.</p>
              </div>
              <div className="rounded-md border border-slate-200 p-4">
                <div className="text-sm font-medium text-slate-900">Client approval</div>
                <p className="mt-2 text-sm leading-6 text-slate-600">The client amends and approves the source-language script before translation and final quality control (Humano).</p>
              </div>
              <div className="rounded-md border border-slate-200 p-4">
                <div className="text-sm font-medium text-slate-900">Translations + Voice Overs</div>
                <p className="mt-2 text-sm leading-6 text-slate-600">Translations and voice overs start after the base guide is approved, followed by a final quality control (Humano) pass.</p>
              </div>
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'faq' && (
        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-sm font-semibold text-slate-900">FAQ</div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {FAQ_ITEMS.map((item) => (
              <div key={item.question} className="rounded-md border border-slate-200 p-4">
                <div className="text-sm font-medium text-slate-900">{item.question}</div>
                <p className="mt-2 text-sm leading-6 text-slate-600">{item.answer}</p>
              </div>
            ))}
          </div>
        </section>
        )}

        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <label className="text-sm font-semibold text-slate-900" htmlFor="onboarding-notes">Internal notes</label>
          <textarea
            id="onboarding-notes"
            value={activeClient.notes || ''}
            onChange={(event) => updateActiveClient({ notes: event.target.value })}
            rows={3}
            placeholder="Next contact, risks, special client requirements..."
            className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
          />
        </section>

        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Save className="h-3.5 w-3.5" />
          {saveStatus}. Internal JSON is saved in <code>onboarding_clients</code>; client-facing JSON is saved in <code>onboarding_public</code>.
          <div className="ml-auto flex items-center gap-2">
            <button type="button" onClick={exportActiveClientJson} className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"><Download className="h-3.5 w-3.5" /> Export JSON</button>
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"><Upload className="h-3.5 w-3.5" /> Import JSON<input type="file" accept="application/json,.json" onChange={importActiveClientJson} className="sr-only" /></label>
          </div>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ClientOnboardingHub;
